import { Controller } from '@hotwired/stimulus';
import {
  fetchPickerConfig,
  loadGooglePickerApi,
  openGoogleDrivePicker,
  driveUrlFromPickerDoc
} from '../lib/google_drive_picker';
import { diffLines, diffCounts, diffHunks } from '../lib/text_diff';
import { renderMarkdown } from '../lib/mini_markdown';
import {
  buildMentionables,
  findMentions,
  insertMention,
  matchMentionables,
  mentionQueryAt
} from '../lib/tab_mentions';
import {
  appsPresentInText,
  insertAppRef,
  removeAppRef
} from '../lib/workspace_apps';
import { insertTerm, removeTerm, textNamesTerm } from '../lib/prompt_terms';
import {
  EDITABLE_KINDS,
  builderChainHtml,
  builderGroupsHtml,
  sheetGraphicHtml
} from '../lib/automate_sheet_graphic';

const SCRIPT_ID_PATTERNS = [
  /script\.google\.com(?:\/u\/\d+)?\/home\/projects\/([a-zA-Z0-9_-]+)/i,
  /script\.google\.com\/d\/([a-zA-Z0-9_-]+)/i,
  /script\.google\.com\/macros\/d\/([a-zA-Z0-9_-]+)/i
];
const MIN_SCRIPT_ID_LENGTH = 20;

function extractScriptId(value) {
  const raw = (value || '').trim();
  if (!raw) return null;

  for (const pattern of SCRIPT_ID_PATTERNS) {
    const match = raw.match(pattern);
    if (match && match[1].length >= MIN_SCRIPT_ID_LENGTH) return match[1];
  }

  if (raw.length >= MIN_SCRIPT_ID_LENGTH && /^[a-zA-Z0-9_-]+$/.test(raw)) return raw;
  return null;
}

const DOT_BASE =
  'w-[44px] h-[44px] rounded-full font-sans text-[17px] font-bold cursor-pointer transition-colors flex items-center justify-center';
const DOT_STATES = {
  done: 'border-0 bg-[#10b981] text-[#022c22] text-[16px]',
  active: 'border-2 border-[#34d399] bg-[rgba(16,185,129,0.12)] text-[#6ee7b7]',
  idle: 'border border-[#1f2937] bg-transparent text-[#334155]'
};

const PILL_ACTIVE =
  'h-[32px] px-[11px] rounded-full border border-[rgba(52,211,153,0.45)] bg-[rgba(16,185,129,0.15)] text-[#6ee7b7] text-[14.5px] font-semibold font-sans';
const PILL_IDLE =
  'h-[32px] px-[11px] rounded-full border border-[#334155] bg-transparent text-[#cbd5e1] text-[14.5px] font-sans';

// Automate writes its own file rather than the user's Code.gs. Code.gs is the
// override: you pick it when the change has to sit next to code that is already
// there (an existing onEdit, say), since Apps Script shares one namespace across
// every file in a project.
const DEFAULT_TARGET_FILE = 'Better';

function normalizeTargetFile(value) {
  const name = String(value || '')
    .trim()
    .replace(/\.gs$/i, '')
    .trim();
  return /^[A-Za-z0-9 _-]{1,60}$/.test(name) ? name : DEFAULT_TARGET_FILE;
}

// Code modes return a pushable file; prose modes return markdown notes.
const CODE_MODES = ['write', 'comment', 'fix'];
// Fix answers in two halves: prose explaining what was wrong (spoken to the user,
// never written into the script) plus, when the fix is a code change, the rewritten
// file. Either half can be absent — a time-zone fix has no code, an obvious
// off-by-one needs little talk — so a Fix turn may land as a code card with notes
// or as a plain prose card.
const HYBRID_MODES = ['fix'];
const PUSHABLE_MODES = ['write', 'comment', 'fix'];
// Modes that never emit executable code: Comment and Plan do write into the
// target file, but only comments. Amber, not emerald.
const SAFE_MODES = ['comment', 'plan'];
const SAFE_NOTICES = {
  comment:
    'Relatively safe \u2014 this writes into your Apps Script, but only comments. No code is added or changed.',
  plan:
    'Relatively safe \u2014 this writes into your Apps Script as one comment block. No code is added or changed.'
};
const MODE_META = {
  write: { verb: 'Wrote', placeholder: 'Email me the new Orders rows every Monday at 9am' },
  comment: { verb: 'Commented', placeholder: 'Explain what each function does (or just press Send)' },
  fix: { verb: 'Fixed', title: 'Fix', placeholder: 'What is not working? (an error, or nothing happening at all)' },
  plan: { title: 'Plan', placeholder: 'What else could this sheet do? (or just press Send)' },
  map: { title: 'Map', placeholder: 'Map the script (or ask about one function)' },
  ask: { title: 'Ask', placeholder: 'Ask anything about this script' }
};
// Delimits the Plan notes block so a later push replaces it instead of stacking
// another copy on top.
const PLAN_NOTES_BEGIN = '// === Better Sheets Automate: Plan notes ===';
const PLAN_NOTES_END = '// === Better Sheets Automate: Plan notes end ===';

// Budget for the sheet-context prefix injected into every prompt.
const MAX_CONTEXT_CHARS = 2000;
const MAX_HEADERS_PER_TAB = 12;
// Tabs the prompt names by @mention are worth more room than the rest.
const MAX_HEADERS_PER_MENTIONED_TAB = 24;
const MAX_MENTION_SUGGESTIONS = 8;
const MAX_MENTION_CHIPS = 6;

const MENTION_CHIP =
  'h-[32px] inline-flex items-center px-[9px] border border-[#1f2937] rounded-[6px] bg-transparent font-mono text-[14px] text-[#94a3b8] hover:text-[#cbd5e1] hover:border-[#334155] hover:bg-[rgba(30,41,59,0.5)] cursor-pointer';

// Identifies one tab across every connected spreadsheet. Falls back to the
// spreadsheet name because a sheet picked up from a saved connection may not
// have its id in hand yet.
function tabKey(spreadsheetId, spreadsheetName, tabName) {
  const owner = spreadsheetId || String(spreadsheetName || 'Spreadsheet').trim().toLowerCase();
  return `${owner} ${String(tabName || '').trim().toLowerCase()}`;
}

const MODE_ORDER = ['write', 'comment', 'plan', 'map', 'ask', 'fix'];

// One-click starters per mode. Write/Append share the automation list; the prose
// modes get prompts that make sense without the user typing anything.
const SUGGESTIONS = {
  write: [
    'Timestamp each new row in Orders',
    'Email me a summary every Monday at 9am',
    'Add a menu item that clears the Log tab',
    'Highlight rows where Total is over 1000',
    'Copy completed rows to an Archive tab',
    'Sort the sheet by date whenever it opens',
    'Remove duplicate rows in the active tab',
    'Email me whenever someone edits the sheet',
    'Add a Mark as done button for the selected row',
    'Auto-fill today\u2019s date when a row is started',
    'Back up this sheet to a new file every week',
    'Send a Slack-style digest of what changed today'
  ],
  comment: [
    'Comment every function',
    'Explain the trickiest function in plain English',
    'Document what each function does to the sheet'
  ],
  plan: [
    'What else could this sheet do?',
    'What is the highest-value automation to build next?',
    'Plan a weekly reporting workflow'
  ],
  map: [
    'Map the whole script',
    'What does onOpen end up calling?',
    'Which parts of my sheet does this write to?'
  ],
  ask: [
    'What does this script actually do?',
    'Which tabs and columns does it touch?',
    'What does this need that is not in the file?'
  ],
  fix: [
    'I ran it and nothing happened',
    'I got an error and I do not know what it means',
    'It runs, but the numbers are wrong',
    'It works when I run it, but not on schedule',
    'It worked last week and stopped'
  ]
};

const PROSE_DEFAULT_PROMPT = {
  plan: 'Look at my sheet and my Apps Script, then plan what to automate next.',
  map: 'Map this Apps Script for me.',
  ask: 'Tell me what this Apps Script does, then suggest what I might want to ask about it.',
  fix: 'Something is not working with this sheet. Help me find out what.',
  comment: 'Add explanatory comments throughout the file you are writing.'
};

const SEND_ENABLED =
  'ml-auto h-[40px] px-4 rounded-lg border-0 bg-[#10b981] text-white text-[15.5px] font-semibold cursor-pointer hover:bg-[#059669] inline-flex items-center gap-2';
const SEND_DISABLED =
  'ml-auto h-[40px] px-4 rounded-lg border-0 bg-[#0f172a] text-[#475569] text-[15.5px] font-semibold cursor-not-allowed inline-flex items-center gap-2';
// The shortcut rides inside the button rather than beside it: it is a label for
// this action, not a second control.
const SEND_SHORTCUT = '<span class="font-mono text-[15px] opacity-70" aria-hidden="true">⌘↵</span>';

const BUILDER_GENERATE_ENABLED =
  'shrink-0 h-[44px] px-4 rounded-lg border-0 bg-[#10b981] text-white text-[15.5px] font-semibold cursor-pointer hover:bg-[#059669]';
const BUILDER_GENERATE_DISABLED =
  'shrink-0 h-[44px] px-4 rounded-lg border-0 bg-[#0f172a] text-[#475569] text-[15.5px] font-semibold cursor-not-allowed';

const TRIGGER_PILL_ON =
  'h-[34px] px-3 rounded-full border border-[var(--automate-accent)] bg-[var(--automate-accent-soft)] text-[var(--automate-accent-text)] text-[14px] font-semibold font-sans cursor-pointer';
const TRIGGER_PILL_OFF =
  'h-[34px] px-3 rounded-full border border-[#1f2937] bg-transparent text-[#94a3b8] text-[14px] font-sans cursor-pointer hover:text-[#cbd5e1] hover:border-[#334155]';

const TRIGGER_OPTION_ON =
  'h-[30px] px-2.5 rounded-full border border-[var(--automate-accent)] bg-[var(--automate-accent-soft)] text-[var(--automate-accent-text)] text-[13.5px] font-semibold font-sans cursor-pointer';
const TRIGGER_OPTION_OFF =
  'h-[30px] px-2.5 rounded-full border border-[#1f2937] bg-transparent text-[#94a3b8] text-[13.5px] font-sans cursor-pointer hover:text-[#cbd5e1] hover:border-[#334155]';

const BUILDER_AND_THEN_BASE =
  'flex-1 h-[44px] rounded-[10px] border-0 font-sans text-[15px] font-bold tracking-[0.06em]';
const BUILDER_AND_THEN_ENABLED =
  `${BUILDER_AND_THEN_BASE} bg-[var(--automate-accent)] text-[var(--automate-accent-ink)] cursor-pointer hover:brightness-110`;
const BUILDER_AND_THEN_DISABLED = `${BUILDER_AND_THEN_BASE} bg-[#0f172a] text-[#475569] cursor-not-allowed`;

const STATE_SWITCH_ON =
  'h-[36px] px-[15px] rounded-[9px] border border-[var(--automate-accent)] bg-[var(--automate-accent-soft)] text-[var(--automate-accent-text)] text-[14.5px] font-semibold font-sans cursor-pointer shadow-[0_0_0_2px_var(--automate-accent-ring)]';
const STATE_SWITCH_OFF =
  'h-[36px] px-[15px] rounded-[9px] border border-[#1f2937] bg-transparent text-[#64748b] text-[14.5px] font-semibold font-sans cursor-pointer hover:text-[#94a3b8] hover:border-[#334155]';

// The confirmation accents offered in the header, emerald first.
const ACCENTS = ['#34d399', '#60a5fa', '#fbbf24', '#f472b6', '#a78bfa'];
const ACCENT_STORAGE_KEY = 'automate:accent';
// Remembered per browser: a member who chose to write code without Google
// lands straight in the workspace next time.
const STANDALONE_STORAGE_KEY = 'automate:standalone';

// How much of a tab the graphic shows before it stops listing columns. The
// service caps what we receive at 24 headers; the graphic is a shape, not a
// table, so it shows fewer and counts the rest.
const MAX_GRAPHIC_HEADERS = 12;
// The cell grid in the explore state is a handle for "a cell", not the data.
const GRAPHIC_ROWS = ['1', '2', '3'];
const GRAPHIC_COLUMNS = ['A', 'B', 'C', 'D'];

export default class extends Controller {
  static targets = [
    // onboarding
    'onboarding',
    'dotGoogle',
    'dotSheet',
    'dotScript',
    'stepConnect',
    'stepSheet',
    'stepScript',
    'sheetError',
    'sheetErrorText',
    'sheetLoading',
    'pickerButton',
    'createSheetButton',
    'scriptInput',
    'scriptError',
    'scriptErrorText',
    'scriptLoading',
    'scriptCaptionSheetName',
    'connectScriptButton',
    // workspace
    'workspace',
    'blankHeadline',
    'turnsList',
    'streamingCard',
    'streamLog',
    'streamActiveLabel',
    'streamHeader',
    'promptInput',
    'sendButton',
    'composer',
    'composerCaret',
    'safeNotice',
    'safeNoticeText',
    'mentionChips',
    'mentionMenu',
    'workspaceApps',
    'workspaceApp',
    // two entry states: type it, or be shown what a sheet can do
    'accentSwatch',
    'stateSwitch',
    'stateSwitchButton',
    'knowState',
    'exploreState',
    'sheetGraphic',
    'sheetPart',
    'builderGroups',
    'builderChain',
    'builderStep',
    'builderOpenBox',
    'builderText',
    'builderTileInput',
    'builderTrigger',
    'builderTriggerOptions',
    'builderTriggerOption',
    'builderAndThen',
    'builderGenerate',
    'builderPreview',
    'statusSheetName',
    'statusSheetLink',
    'statusTabs',
    'suggestionChips',
    'refreshButton',
    'refreshLabel',
    'composerError',
    'composerErrorText',
    // mode pills
    'modePills',
    'modePill',
    // overlays
    'scrim',
    'diffModal',
    'diffPosition',
    'diffFileName',
    'diffBody',
    'helpModal',
    'contextPanel',
    'contextSheetName',
    'contextSheetId',
    'contextTabsList',
    'addSpreadsheetError',
    'setupPanel',
    'instructionsPanel',
    'instructionsText',
    'instructionsSavedLabel',
    'sheetLinksJson',
    'codeWriteModal',
    'codeWriteTitle',
    'codeWriteText',
    'codeWriteNote',
    'codeWriteConfirmButton',
    'membershipModal',
    'betaModal',
    'standaloneCodeBox',
    'standaloneCodeInput',
    'standaloneCodeToggle',
    'connectLink',
    'chatColumn',
    'codePanel',
    'codePanelFile',
    'codePanelMeta',
    'codePanelBody',
    'codePanelTab',
    'codePanelCopy'
  ];

  static values = {
    paidMember: { type: Boolean, default: false },
    pickerConfigUrl: String,
    pushUrl: String,
    agentUrl: String,
    createSpreadsheetUrl: String,
    scriptPreviewUrl: String,
    spreadsheetPreviewUrl: String,
    connectionUrl: String,
    instructionsUrl: String,
    connected: Boolean,
    pickerConfigured: Boolean,
    agentConfigured: Boolean,
    email: String,
    lastSpreadsheetId: String,
    lastScriptId: String,
    targetFile: String,
    initialStep: { type: String, default: 'connect' }
  };

  connect() {
    this.step = this.hasInitialStepValue ? this.initialStepValue : 'connect';
    this.layer = null; // null | diff | help | context | setup
    this.mode = 'write';

    this.spreadsheetId = null;
    this.spreadsheetName = null;
    this.spreadsheetUrl = null;
    this.sheetTabs = [];
    this.extraSpreadsheets = [];

    this.targetFile = normalizeTargetFile(this.hasTargetFileValue ? this.targetFileValue : '');

    this.scriptId = null;
    this.scriptCode = null;
    this.workingCode = null;
    // No Google connection: the member copies code out by hand. See enterStandalone().
    this.standalone = false;
    // Which turn the code panel shows, and which of its tabs.
    this.panelTurnIndex = null;
    this.panelTab = 'code';
    // Every other .gs in the same project, read-only context for the agent.
    this.otherFiles = [];

    this.turns = [];
    this.phase = 'idle'; // idle | writing
    this.streamLog = [];
    this.streamPhase = 0;
    this.streamFnName = null;
    this.streamBuffer = '';
    this.sentExistingCode = false;
    this.streamController = null;

    this.currentDiffHunks = [];
    this.currentDiffIndex = 0;

    // Asked once per sheet, per session, before Automate writes a file it does
    // not own.
    this.codeWriteConfirmed = false;
    this.pendingCodeWrite = null;

    this.pickerOpening = false;
    this.sheetFetchToken = 0;
    this.scriptFetchToken = 0;

    // @mention autocomplete in the composer.
    this.mentionMatches = [];
    this.mentionRange = null;
    this.mentionIndex = 0;

    this.sheetLinksMap = this.buildSheetLinksMap(this.loadSheetLinks());

    // know: type what you want. explore: assemble it from tiles.
    this.uiMode = 'know';
    this.accent = this.loadAccent();
    // Which tab the sheet graphic is showing. Derived from the prompt when it
    // names one; a click sets it directly.
    this.graphicTab = null;
    // Parts the user has switched off, so they are left out of the context
    // prefix. Keys are tabKey() for tabs, 'file' for the whole spreadsheet.
    this.excludedContext = new Set();
    // The open box (tiles plus whatever is typed after them) and the locked
    // steps of the explore-state chain.
    this.builder = { trigger: null, current: [], steps: [], text: '' };
    this.draggingTile = null;

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.layer) this.closeLayer();
    };
    document.addEventListener('keydown', this.onKeydown);

    if (!this.paidMemberValue) {
      this.boundPreviewCapture = this.onPreviewCapture.bind(this);
      this.element.addEventListener('click', this.boundPreviewCapture, true);
      this.element.addEventListener('keydown', this.boundPreviewCapture, true);
    }

    this.determineInitialStep();
    this.renderAll();
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKeydown);
    if (this.boundPreviewCapture) {
      this.element.removeEventListener('click', this.boundPreviewCapture, true);
      this.element.removeEventListener('keydown', this.boundPreviewCapture, true);
    }
    if (this.streamController) this.streamController.abort();
  }

  onPreviewCapture(event) {
    if (this.paidMemberValue) return;

    const escapeEl = event.target.closest?.('[data-automate-escape]');
    if (escapeEl) return;

    if (event.type === 'keydown') {
      if (event.key === 'Tab') return;
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;

      if (event.key === 'Escape') {
        if (this.isMembershipModalOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.closeMembershipModal();
        }
        return;
      }
    }

    if (this.isMembershipModalOpen()) return;

    event.preventDefault();
    event.stopPropagation();
    this.openMembershipModal();
  }

  openMembershipModal() {
    if (!this.hasMembershipModalTarget) return;
    if (typeof this.membershipModalTarget.showModal === 'function') {
      this.membershipModalTarget.showModal();
    }
  }

  closeMembershipModal() {
    if (!this.hasMembershipModalTarget) return;
    if (typeof this.membershipModalTarget.close === 'function') {
      this.membershipModalTarget.close();
    }
  }

  openBetaModal(event) {
    if (event) event.preventDefault();
    if (!this.hasBetaModalTarget) return;
    if (typeof this.betaModalTarget.showModal === 'function') {
      this.betaModalTarget.showModal();
    }
  }

  closeBetaModal() {
    if (!this.hasBetaModalTarget) return;
    if (typeof this.betaModalTarget.close === 'function') {
      this.betaModalTarget.close();
    }
  }

  isMembershipModalOpen() {
    return this.hasMembershipModalTarget && this.membershipModalTarget.open;
  }

  determineInitialStep() {
    if (!this.paidMemberValue) {
      this.applyPreviewWorkspace();
      return;
    }

    if (!this.isGoogleConnected()) {
      if (this.readStandalonePreference()) {
        this.enterStandalone();
        return;
      }
      this.step = 'connect';
      return;
    }

    const lastSheet = this.hasLastSpreadsheetIdValue ? this.lastSpreadsheetIdValue : null;
    if (!lastSheet) {
      this.step = 'sheet';
      return;
    }

    // Only use the per-sheet link map. lastScriptId can belong to a different
    // sheet after a switch, so it must not skip the script step alone.
    const saved = this.savedSheetLinkFor(lastSheet);
    const scriptId = saved?.script_id;

    this.spreadsheetId = lastSheet;
    this.spreadsheetName = saved?.spreadsheet_name || 'Your spreadsheet';
    this.spreadsheetUrl = `https://docs.google.com/spreadsheets/d/${lastSheet}/edit`;

    if (scriptId) {
      this.scriptId = scriptId;
      if (saved?.target_file) this.targetFile = normalizeTargetFile(saved.target_file);
      this.step = 'ready';
      this.fetchSpreadsheetContext();
      this.fetchScriptContent(scriptId, { silent: true });
    } else {
      this.step = 'script';
      this.fetchSpreadsheetContext();
    }
  }

  // Soft paywall: show the ready workspace so visitors see how Automate looks.
  // Google connect onboarding stays for paid members who actually use it.
  applyPreviewWorkspace() {
    this.step = 'ready';
    this.spreadsheetId = 'preview';
    this.spreadsheetName = 'Orders';
    this.spreadsheetUrl = null;
    this.sheetTabs = [
      { name: 'Orders', headers: ['Date', 'Customer', 'Total'], headerColumns: ['A', 'B', 'C'] },
      { name: 'Log', headers: ['When', 'Event'], headerColumns: ['A', 'B'] },
      { name: 'Archive', headers: ['Date', 'Customer', 'Total'], headerColumns: ['A', 'B', 'C'] }
    ];
  }

  isGoogleConnected() {
    if (this.connectedValue) return true;
    return Boolean(this.hasEmailValue && this.emailValue?.trim());
  }

  resolveStep(step) {
    if (step === 'connect' && this.isGoogleConnected()) return 'sheet';
    return step;
  }

  loadSheetLinks() {
    if (!this.hasSheetLinksJsonTarget) return [];
    try {
      return JSON.parse(this.sheetLinksJsonTarget.textContent || '[]');
    } catch {
      return [];
    }
  }

  buildSheetLinksMap(links) {
    const map = {};
    const list = Array.isArray(links) ? links : Object.values(links || {});
    for (const link of list) {
      if (link?.spreadsheet_id) map[link.spreadsheet_id] = link;
    }
    return map;
  }

  savedSheetLinkFor(spreadsheetId) {
    if (!spreadsheetId) return null;
    return this.sheetLinksMap?.[spreadsheetId] || null;
  }

  rememberSheetLink(spreadsheetId, scriptId, targetFile = null) {
    if (!spreadsheetId || !scriptId) return;
    this.sheetLinksMap[spreadsheetId] = {
      spreadsheet_id: spreadsheetId,
      script_id: scriptId,
      target_file: targetFile || this.targetFile,
      spreadsheet_name: this.spreadsheetName
    };
  }

  async applySpreadsheetSelection({ id, name, url = null, clearThread = false }) {
    this.spreadsheetId = id;
    this.spreadsheetName = name;
    this.spreadsheetUrl = url || `https://docs.google.com/spreadsheets/d/${id}/edit`;
    this.sheetTabs = [];
    this.resetSheetGraphicState();
    this.codeWriteConfirmed = false;
    this.scriptId = null;
    this.scriptCode = null;
    this.workingCode = null;
    this.otherFiles = [];
    if (this.hasScriptInputTarget) this.scriptInputTarget.value = '';
    this.clearSheetError();
    this.clearScriptError();

    if (clearThread) {
      this.turns = [];
      if (this.hasPromptInputTarget) this.promptInputTarget.value = '';
    }

    // Ask the server while we show a short "looking up" state. The local map can
    // be stale after Start over / Clear, but the DB still has the sheet→script
    // link. Waiting on that response is what lets us skip the script URL step.
    this.step = 'script';
    this.renderAll();
    this.setScriptLookupState(true);
    this.fetchSpreadsheetContext();

    const local = this.savedSheetLinkFor(id);
    const data = await this.persistConnection(id, local?.script_id || null, {
      spreadsheetName: name
    });

    const scriptId = data?.script_id || local?.script_id;
    if (!scriptId) {
      this.setScriptLookupState(false);
      this.step = 'script';
      this.renderAll();
      return;
    }

    this.scriptId = scriptId;
    if (data?.target_file) this.targetFile = normalizeTargetFile(data.target_file);
    else if (local?.target_file) this.targetFile = normalizeTargetFile(local.target_file);
    this.rememberSheetLink(id, scriptId, this.targetFile);

    try {
      await this.fetchScriptContent(scriptId, { silent: true });
      this.setScriptLookupState(false);
      this.step = 'ready';
      this.renderAll();
    } catch {
      this.scriptId = null;
      this.scriptCode = null;
      this.workingCode = null;
      this.otherFiles = [];
      this.setScriptLookupState(false);
      this.step = 'script';
      this.renderAll();
    }
  }

  setScriptLookupState(looking) {
    if (this.hasScriptLoadingTarget) {
      this.scriptLoadingTarget.textContent = looking
        ? 'We already have an Apps Script for this sheet. Loading it…'
        : 'Fetching Apps Script…';
      this.scriptLoadingTarget.classList.toggle('hidden', !looking);
    }
    if (this.hasScriptInputTarget) this.scriptInputTarget.disabled = looking;
    if (this.hasConnectScriptButtonTarget) {
      this.connectScriptButtonTarget.disabled = looking;
      this.connectScriptButtonTarget.textContent = looking ? 'Loading…' : 'Connect';
    }
  }

  // ---------- render ----------

  renderAll() {
    this.renderAccent();
    this.renderDots();
    this.renderOnboardingSteps();
    this.renderModePills();
    this.renderOnboarding();
    this.setLayer(this.layer);
  }

  renderOnboarding() {
    const showOnboarding = this.step !== 'ready';
    if (this.hasOnboardingTarget) this.onboardingTarget.classList.toggle('hidden', !showOnboarding);
    if (this.hasWorkspaceTarget) this.workspaceTarget.classList.toggle('hidden', showOnboarding);
    if (this.hasModePillsTarget) this.modePillsTarget.classList.toggle('hidden', showOnboarding);
    if (!showOnboarding) this.renderWorkspace();
  }

  renderOnboardingSteps() {
    if (this.hasStepConnectTarget) this.stepConnectTarget.classList.toggle('hidden', this.step !== 'connect');
    if (this.hasStepSheetTarget) this.stepSheetTarget.classList.toggle('hidden', this.step !== 'sheet');
    if (this.hasStepScriptTarget) this.stepScriptTarget.classList.toggle('hidden', this.step !== 'script');
    if (this.hasScriptCaptionSheetNameTarget) {
      const caption = this.scriptCaptionSheetNameTarget;
      caption.textContent = this.spreadsheetName || 'your sheet';
      // Only a real link once we know the sheet; otherwise plain text.
      if (this.spreadsheetUrl) {
        caption.href = this.spreadsheetUrl;
        caption.removeAttribute('aria-disabled');
        caption.title = 'Open this spreadsheet in a new tab';
      } else {
        caption.removeAttribute('href');
        caption.setAttribute('aria-disabled', 'true');
        caption.removeAttribute('title');
      }
    }
  }

  renderDots() {
    if (this.hasDotGoogleTarget) {
      const done = this.step !== 'connect';
      this.dotGoogleTarget.className = `${DOT_BASE} ${DOT_STATES[done ? 'done' : 'active']}`;
      this.dotGoogleTarget.textContent = done ? '✓' : 'G';
    }
    if (this.hasDotSheetTarget) {
      const state = this.step === 'sheet' ? 'active' : this.step === 'connect' ? 'idle' : 'done';
      this.dotSheetTarget.className = `${DOT_BASE} ${DOT_STATES[state]} font-mono`;
      this.dotSheetTarget.textContent = this.step === 'sheet' || this.step === 'connect' ? '▦' : '✓';
    }
    if (this.hasDotScriptTarget) {
      const state = this.step === 'script' ? 'active' : 'idle';
      this.dotScriptTarget.className = `${DOT_BASE} ${DOT_STATES[state]} font-mono`;
      this.dotScriptTarget.textContent = '{ }';
    }
  }

  renderModePills() {
    if (!this.hasModePillTarget) return;
    this.modePillTargets.forEach((pill) => {
      const active = pill.dataset.mode === this.mode;
      const safePill = active && SAFE_MODES.includes(pill.dataset.mode);
      pill.className = active ? PILL_ACTIVE : PILL_IDLE;
      pill.style.borderColor = safePill ? 'rgba(252,211,77,0.5)' : '';
      pill.style.backgroundColor = safePill ? 'rgba(252,211,77,0.15)' : '';
      pill.style.color = safePill ? '#fde68a' : '';
      pill.setAttribute('aria-pressed', String(pill.dataset.mode === this.mode));
    });
    if (this.hasPromptInputTarget) {
      this.promptInputTarget.placeholder = MODE_META[this.mode]?.placeholder || '';
    }
    if (this.hasSuggestionChipsTarget) this.renderSuggestionChips();
  }

  renderSuggestionChips() {
    if (!this.hasSuggestionChipsTarget) return;
    const list = SUGGESTIONS[this.mode] || [];
    this.suggestionChipsTarget.innerHTML = list
      .map(
        (text) =>
          `<button type="button" data-action="click->automate#useSuggestion" data-prompt="${this.escapeHtml(text)}" class="border border-[#1f2937] rounded-full px-3.5 py-[7px] bg-transparent text-[15px] text-[#94a3b8] font-sans cursor-pointer hover:border-[#334155] hover:text-[#cbd5e1]">${this.escapeHtml(text)}</button>`
      )
      .join('');
  }

  // Safe mode is visually distinct so it is obvious at a glance that nothing
  // will be written to the user's script.
  applySafeModeChrome() {
    const safe = SAFE_MODES.includes(this.mode);
    // Inline styles rather than Tailwind classes: these arbitrary values appear
    // only here in JS, so Tailwind's content scan would not emit them.
    if (this.hasComposerTarget) {
      this.composerTarget.style.borderColor = safe ? 'rgba(252,211,77,0.55)' : '';
    }
    if (this.hasComposerCaretTarget) {
      this.composerCaretTarget.style.color = safe ? '#fcd34d' : '';
    }
    if (this.hasSafeNoticeTarget) {
      this.safeNoticeTarget.classList.toggle('hidden', !safe);
      if (safe && this.hasSafeNoticeTextTarget) {
        this.safeNoticeTextTarget.textContent = SAFE_NOTICES[this.mode] || '';
      }
    }
  }

  isCodeMode(mode = this.mode) {
    return CODE_MODES.includes(mode);
  }

  isHybridMode(mode = this.mode) {
    return HYBRID_MODES.includes(mode);
  }

  renderWorkspace() {
    this.updateBlankState();
    this.renderTurns();
    this.renderStreaming();
    this.renderStatusLine();
    this.renderMentionChips();
    this.renderSuggestionChips();
    this.renderUiMode();
    this.updateSendButton();
  }

  updateBlankState() {
    const blank = this.turns.length === 0 && this.phase !== 'writing';
    if (this.hasBlankHeadlineTarget) {
      this.blankHeadlineTarget.classList.toggle('hidden', !blank || this.uiMode !== 'know');
    }
    if (this.hasSuggestionChipsTarget) {
      this.suggestionChipsTarget.classList.toggle('hidden', !blank || this.uiMode !== 'know');
    }
  }

  renderStatusLine() {
    if (this.hasRefreshButtonTarget) this.refreshButtonTarget.classList.toggle('hidden', this.standalone);
    if (this.hasConnectLinkTarget) this.connectLinkTarget.classList.toggle('hidden', !this.standalone);
    if (this.hasStandaloneCodeToggleTarget) this.standaloneCodeToggleTarget.classList.toggle('hidden', !this.standalone);
    if (this.hasStandaloneCodeBoxTarget && !this.standalone) this.standaloneCodeBoxTarget.classList.add('hidden');
    if (this.hasStatusSheetNameTarget) this.statusSheetNameTarget.textContent = this.spreadsheetName || 'Spreadsheet';
    if (this.hasStatusSheetLinkTarget) {
      this.statusSheetLinkTarget.href = this.spreadsheetUrl || '#';
    }
    if (this.hasStatusTabsTarget) {
      const names = this.sheetTabs.map((tab) => tab.name);
      this.statusTabsTarget.textContent = names.length ? names.join(' · ') : '—';
    }
  }

  // ---------- tab mentions ----------

  // Every tab a prompt is allowed to name, primary spreadsheet first.
  get mentionables() {
    return buildMentionables([
      { id: this.spreadsheetId, name: this.spreadsheetName, tabs: this.sheetTabs, primary: true },
      ...this.extraSpreadsheets
    ]);
  }

  renderMentionChips() {
    if (!this.hasMentionChipsTarget) return;

    const all = this.mentionables;
    const shown = all.slice(0, MAX_MENTION_CHIPS);
    const hidden = all.length - shown.length;

    const chips = shown.map(
      (mentionable) => `
        <button type="button"
                data-action="click->automate#insertTabMention"
                data-mention-token="${this.escapeHtml(mentionable.token)}"
                title="Reference ${this.escapeHtml(mentionable.tabName)} in your prompt"
                class="${MENTION_CHIP}">@${this.escapeHtml(mentionable.token)}</button>`
    );

    if (hidden > 0) {
      chips.push(`
        <button type="button" data-action="click->automate#openLayer" data-layer="context"
                title="See every tab Automate can read"
                class="${MENTION_CHIP} text-[#64748b]">+${hidden} more</button>`);
    }

    this.mentionChipsTarget.innerHTML = chips.join('');
  }

  // Clicking a chip (composer or context panel) drops @Tab at the caret rather
  // than replacing what has been typed, so a tab can be named mid-sentence.
  insertTabMention(event) {
    const token = event.currentTarget.dataset.mentionToken;
    if (!token || !this.hasPromptInputTarget) return;

    if (this.layer === 'context') this.closeLayer();

    insertMention(this.promptInputTarget, token);
    this.promptInputTarget.focus();
    this.closeMentionMenu();
    this.updateSendButton();
  }

  // ---------- @ autocomplete ----------

  updateMentionMenu() {
    if (!this.hasPromptInputTarget || !this.hasMentionMenuTarget) return;

    const input = this.promptInputTarget;
    const range = mentionQueryAt(input.value, input.selectionStart ?? input.value.length);
    if (!range) return this.closeMentionMenu();

    const matches = matchMentionables(this.mentionables, range.query).slice(0, MAX_MENTION_SUGGESTIONS);
    if (!matches.length) return this.closeMentionMenu();

    this.mentionRange = range;
    // Keep the highlight on the same tab while the query narrows, so typing
    // another letter never selects something else out from under you.
    const previous = this.mentionMatches[this.mentionIndex]?.token;
    this.mentionMatches = matches;
    const kept = matches.findIndex((match) => match.token === previous);
    this.mentionIndex = kept === -1 ? 0 : kept;

    this.renderMentionMenu();
  }

  renderMentionMenu() {
    if (!this.hasMentionMenuTarget) return;

    this.mentionMenuTarget.innerHTML = this.mentionMatches
      .map((match, index) => {
        const active = index === this.mentionIndex;
        const headers = match.headers.slice(0, 4).join(' · ');
        const detail = match.primary ? headers : `${match.sheetName}${headers ? ` — ${headers}` : ''}`;
        return `
          <button type="button" role="option" aria-selected="${active}"
                  data-action="mousedown->automate#chooseMentionFromMenu"
                  data-mention-index="${index}"
                  class="w-full text-left px-3 py-2 flex flex-col gap-0.5 cursor-pointer ${
                    active ? 'bg-[rgba(16,185,129,0.14)]' : 'hover:bg-[rgba(30,41,59,0.6)]'
                  }">
            <span class="font-mono text-[14.5px] ${active ? 'text-[#6ee7b7]' : 'text-[#e2e8f0]'}">@${this.escapeHtml(
              match.token
            )}</span>
            ${
              detail
                ? `<span class="text-[13px] leading-[1.5] text-[#64748b] truncate">${this.escapeHtml(detail)}</span>`
                : ''
            }
          </button>`;
      })
      .join('');

    this.mentionMenuTarget.classList.remove('hidden');
  }

  closeMentionMenu() {
    this.mentionMatches = [];
    this.mentionRange = null;
    this.mentionIndex = 0;
    if (this.hasMentionMenuTarget) {
      this.mentionMenuTarget.classList.add('hidden');
      this.mentionMenuTarget.innerHTML = '';
    }
  }

  get mentionMenuOpen() {
    return this.mentionMatches.length > 0;
  }

  // mousedown, not click: the textarea would otherwise blur first and close the
  // menu before the click ever landed.
  chooseMentionFromMenu(event) {
    event.preventDefault();
    const index = Number(event.currentTarget.dataset.mentionIndex);
    this.chooseMention(Number.isNaN(index) ? 0 : index);
  }

  chooseMention(index) {
    const match = this.mentionMatches[index];
    if (!match || !this.hasPromptInputTarget) return;

    insertMention(this.promptInputTarget, match.token, this.mentionRange);
    this.promptInputTarget.focus();
    this.closeMentionMenu();
    this.updateSendButton();
  }

  moveMentionSelection(step) {
    const count = this.mentionMatches.length;
    if (!count) return;
    this.mentionIndex = (this.mentionIndex + step + count) % count;
    this.renderMentionMenu();
  }

  promptBlur() {
    // Deferred so a mousedown on the menu still resolves to a selection.
    setTimeout(() => this.closeMentionMenu(), 120);
  }

  // ---------- standalone (no Google) ----------

  // Google still gates sign-in to approved accounts while the app is in
  // review, so members can skip it: nothing is read from or written to a
  // sheet, and each result is copied into Extensions → Apps Script by hand.
  useWithoutGoogle(event) {
    if (event) event.preventDefault();
    this.closeBetaModal();
    this.enterStandalone();
    this.writeStandalonePreference(true);
    this.renderAll();
  }

  enterStandalone() {
    this.standalone = true;
    this.step = 'ready';
    this.spreadsheetId = null;
    this.spreadsheetName = 'No sheet connected';
    this.spreadsheetUrl = null;
    this.sheetTabs = [];
    this.extraSpreadsheets = [];
    this.scriptId = null;
    const pasted = this.hasStandaloneCodeInputTarget ? this.standaloneCodeInputTarget.value : '';
    this.scriptCode = pasted;
    this.workingCode = pasted;
    this.otherFiles = [];
  }

  leaveStandalone(event) {
    if (event) event.preventDefault();
    this.standalone = false;
    this.writeStandalonePreference(false);
    this.step = this.isGoogleConnected() ? 'sheet' : 'connect';
    this.renderAll();
  }

  toggleStandaloneCode() {
    if (!this.hasStandaloneCodeBoxTarget) return;
    const open = this.standaloneCodeBoxTarget.classList.toggle('hidden');
    if (!open && this.hasStandaloneCodeInputTarget) this.standaloneCodeInputTarget.focus();
  }

  // Whatever is in the box is the file as it stands in their sheet: later
  // turns diff against it and Write mode keeps it.
  standaloneCodeChanged() {
    if (!this.standalone || !this.hasStandaloneCodeInputTarget) return;
    const value = this.standaloneCodeInputTarget.value;
    this.scriptCode = value;
    this.workingCode = value;
  }

  readStandalonePreference() {
    try {
      return window.localStorage?.getItem(STANDALONE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  writeStandalonePreference(on) {
    try {
      if (on) window.localStorage?.setItem(STANDALONE_STORAGE_KEY, '1');
      else window.localStorage?.removeItem(STANDALONE_STORAGE_KEY);
    } catch {
      // storage blocked: the choice simply does not persist
    }
  }

  // ---------- code panel (standalone) ----------

  // With no Google there is nothing to push to, so the file gets its own
  // panel on the right and the turns read as a chat on the left.
  get codeTurnIndexes() {
    return this.turns.map((turn, index) => (turn.kind === 'code' ? index : -1)).filter((index) => index >= 0);
  }

  isSplit() {
    return this.standalone && this.codeTurnIndexes.length > 0;
  }

  panelTurn() {
    const indexes = this.codeTurnIndexes;
    if (!indexes.length) return null;
    if (!indexes.includes(this.panelTurnIndex)) this.panelTurnIndex = indexes[indexes.length - 1];
    return this.turns[this.panelTurnIndex];
  }

  versionNumber(index) {
    return this.codeTurnIndexes.indexOf(index) + 1;
  }

  renderSplit() {
    const split = this.isSplit();
    if (this.hasWorkspaceTarget) {
      const ws = this.workspaceTarget.classList;
      ws.toggle('flex-col', !split);
      ws.toggle('items-center', !split);
      ws.toggle('flex-row', split);
      ws.toggle('items-start', split);
      ws.toggle('gap-6', split);
    }
    if (this.hasChatColumnTarget) {
      this.chatColumnTarget.classList.toggle('w-[440px]', split);
      this.chatColumnTarget.classList.toggle('shrink-0', split);
      this.chatColumnTarget.classList.toggle('w-full', !split);
    }
    if (this.hasCodePanelTarget) {
      this.codePanelTarget.classList.toggle('hidden', !split);
      this.codePanelTarget.classList.toggle('flex', split);
    }
    if (this.hasPromptInputTarget) {
      const input = this.promptInputTarget;
      if (!input.dataset.defaultPlaceholder) input.dataset.defaultPlaceholder = input.placeholder;
      input.placeholder = split ? "Ask for a change…" : input.dataset.defaultPlaceholder;
    }
    if (split) this.renderCodePanel();
  }

  showTurnInPanel(event) {
    this.panelTurnIndex = Number(event.currentTarget.dataset.turnIndex);
    this.panelTab = 'code';
    this.renderTurns();
  }

  setPanelTab(event) {
    this.panelTab = event.currentTarget.dataset.panelTab || 'code';
    this.renderCodePanel();
  }

  renderCodePanel() {
    const turn = this.panelTurn();
    if (!turn) return;
    const file = `${turn.file || this.targetFile}.gs`;
    const lines = turn.code.split('\n').length;

    if (this.hasCodePanelFileTarget) this.codePanelFileTarget.textContent = file;
    if (this.hasCodePanelMetaTarget) {
      this.codePanelMetaTarget.textContent = `Version ${this.versionNumber(this.panelTurnIndex)} · ${lines} lines · ${turn.delta}`;
    }
    if (this.hasCodePanelCopyTarget) this.codePanelCopyTarget.dataset.turnIndex = String(this.panelTurnIndex);
    this.codePanelTabTargets.forEach((tab) => {
      const on = tab.dataset.panelTab === this.panelTab;
      tab.setAttribute('aria-selected', String(on));
      tab.classList.toggle('text-[#f1f5f9]', on);
      tab.classList.toggle('border-[#34d399]', on);
      tab.classList.toggle('text-[#64748b]', !on);
      tab.classList.toggle('border-transparent', !on);
    });
    if (!this.hasCodePanelBodyTarget) return;

    const ops = diffLines(turn.previousCode || '', turn.code);
    const line = (text, tone) =>
      `<div class="px-4 whitespace-pre ${tone}">${this.escapeHtml(text) || ' '}</div>`;

    if (this.panelTab === 'changed') {
      const changed = ops.some((op) => op.type !== 'equal');
      this.codePanelBodyTarget.innerHTML = changed
        ? `<div class="py-3 font-mono text-[14px] leading-[1.7]">${diffHunks(ops, 2)
            .map((hunk) =>
              hunk
                .map((op) =>
                  op.type === 'add'
                    ? line(`+ ${op.line}`, 'bg-[rgba(16,185,129,0.12)] text-[#6ee7b7]')
                    : op.type === 'remove'
                    ? line(`− ${op.line}`, 'bg-[rgba(248,113,113,0.1)] text-[#fca5a5]')
                    : line(`  ${op.line}`, 'text-[#64748b]')
                )
                .join('')
            )
            .join('<div class="my-2 border-t border-[#1f2937]"></div>')}</div>`
        : `<div class="px-4 py-6 text-[15px] text-[#64748b]">No changes from the code you started with.</div>`;
      return;
    }

    if (this.panelTab === 'install') {
      this.codePanelBodyTarget.innerHTML = `
        <ol class="m-0 px-8 py-5 flex flex-col gap-3 text-[15.5px] leading-[1.6] text-[#cbd5e1] list-decimal">
          <li>Open your sheet, then <span class="text-[#f1f5f9]">Extensions → Apps Script</span>.</li>
          <li>Open <span class="font-mono text-[#6ee7b7]">${this.escapeHtml(file)}</span> on the left. No such file? Click <span class="text-[#f1f5f9]">+</span> → Script and name it <span class="font-mono">${this.escapeHtml(file.replace(/\.gs$/, ''))}</span>.</li>
          <li>Select everything in it and paste this version over it.</li>
          <li>Save, then reload your sheet. The first run asks you to approve permissions.</li>
        </ol>`;
      return;
    }

    // Code: the whole file, with this version's new lines tinted.
    this.codePanelBodyTarget.innerHTML = `<div class="py-3 font-mono text-[14px] leading-[1.7] text-[#cbd5e1]">${ops
      .filter((op) => op.type !== 'remove')
      .map((op) => line(op.line, op.type === 'add' && turn.previousCode ? 'bg-[rgba(16,185,129,0.12)]' : ''))
      .join('')}</div>`;
  }

  downloadPanelCode() {
    const turn = this.panelTurn();
    if (!turn) return;
    const blob = new Blob([turn.code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${turn.file || this.targetFile}.gs`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // A code turn as a chat message: what was asked, a short reply, and a
  // chip that opens that version in the panel.
  standaloneTurnHtml(turn, index) {
    const open = index === this.panelTurnIndex;
    const notes = turn.notes
      ? `<div class="automate-prose min-w-0 mb-2">${renderMarkdown(turn.notes)}</div>`
      : '';
    const lost = turn.lostFns?.length
      ? `<div class="mt-2 rounded-lg border border-[rgba(248,113,113,0.5)] bg-[rgba(127,29,29,0.25)] px-3 py-2 text-[14.5px] leading-[1.5] text-[#fca5a5]">This version drops <span class="font-mono">${this.escapeHtml(
          turn.lostFns.map((name) => `${name}()`).join(' · ')
        )}</span>. Ask again and say to keep everything already in the file.</div>`
      : '';
    return `
      <div class="w-full flex flex-col gap-3 animate-[om-rise_0.35s_ease-out]" data-turn-index="${index}">
        <div class="self-end max-w-[85%] border border-[#1f2937] rounded-[12px] bg-[rgba(30,41,59,0.6)] px-3.5 py-2.5 text-[15.5px] leading-[1.5] text-[#f1f5f9]">${this.escapeHtml(turn.prompt)}</div>
        <div class="text-[15.5px] leading-[1.55] text-[#cbd5e1]">
          ${notes}
          <span class="font-semibold text-[#f1f5f9]">${this.escapeHtml(turn.title)}</span>
          <span class="font-mono text-[13.5px] text-[#64748b]">${this.escapeHtml(turn.fns)}</span>
          ${lost}
          <div class="mt-2">
            <button type="button" data-action="click->automate#showTurnInPanel" data-turn-index="${index}"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border bg-transparent text-[13.5px] font-sans cursor-pointer ${
                      open ? 'border-[rgba(52,211,153,0.5)] text-[#34d399]' : 'border-[#334155] text-[#94a3b8] hover:text-[#cbd5e1]'
                    }">Version ${this.versionNumber(index)}${open ? ' · open' : ''}</button>
          </div>
        </div>
      </div>`;
  }

  // ---------- onboarding actions ----------

  goStep(event) {
    const step = event.currentTarget.dataset.step;
    if (!step) return;
    this.step = this.resolveStep(step);
    this.renderDots();
    this.renderOnboardingSteps();
  }

  // The picker is the only way a sheet gets in. It is also what grants us
  // access: drive.file covers exactly what the user picks here, so both the
  // onboarding step and the context panel go through this.
  async pickSpreadsheet({ onPick, onError }) {
    if (this.pickerOpening) return;

    if (!this.isGoogleConnected()) {
      onError('Connect Google first.');
      return;
    }
    if (!this.pickerConfiguredValue) {
      onError('Google Picker is not configured on this server. Use Create a new one instead.');
      return;
    }

    this.pickerOpening = true;
    if (this.hasPickerButtonTarget) this.pickerButtonTarget.disabled = true;

    try {
      const config = await fetchPickerConfig(this.pickerConfigUrlValue);
      await loadGooglePickerApi();

      openGoogleDrivePicker({
        config,
        spreadsheetsOnly: true,
        onPick: (doc) => {
          if (!doc?.id) return;
          if (doc.mimeType && doc.mimeType !== 'application/vnd.google-apps.spreadsheet') {
            onError('Pick a Google Spreadsheet, not a folder or another file type.');
            return;
          }
          onPick(doc);
        },
        onError: (message) => {
          const text = message || 'Google Picker error.';
          if (/developer key|api key|invalid key/i.test(text)) {
            onError(
              'Google Picker rejected the API key. Check ONLY_SHEETS_GOOGLE_API_KEY and allowed referrers, or use Create a new one instead.'
            );
            return;
          }
          onError(text);
        }
      });
    } catch (error) {
      onError(error.message || 'Could not open Google Picker.');
    } finally {
      this.pickerOpening = false;
      if (this.hasPickerButtonTarget) {
        this.pickerButtonTarget.disabled = !this.isGoogleConnected() || !this.pickerConfiguredValue;
      }
    }
  }

  async openPicker(event) {
    event?.preventDefault();
    this.clearSheetError();

    await this.pickSpreadsheet({
      onPick: (doc) => {
        this.selectSpreadsheet(doc.id, doc.name || doc.id, driveUrlFromPickerDoc(doc));
      },
      onError: (message) => this.showSheetError(message)
    });
  }

  async createSpreadsheet(event) {
    event?.preventDefault();
    this.clearSheetError();
    if (!this.isGoogleConnected()) {
      this.showSheetError('Connect Google first.');
      return;
    }

    if (this.hasCreateSheetButtonTarget) {
      this.createSheetButtonTarget.disabled = true;
      this.createSheetButtonTarget.textContent = 'Creating…';
    }

    try {
      const res = await fetch(this.createSpreadsheetUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({})
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.error || `Create failed (${res.status})`);
        err.helpUrl = data.help_url;
        err.helpLabel = data.help_label;
        throw err;
      }

      const spreadsheetId = data.spreadsheet_id || data.spreadsheetId;
      if (!spreadsheetId) throw new Error('Server did not return a spreadsheet ID. Try again.');

      this.selectSpreadsheet(spreadsheetId, data.name || 'Untitled spreadsheet', data.url);
    } catch (error) {
      this.showSheetError(error.message || 'Could not create spreadsheet.', error.helpUrl, error.helpLabel);
    } finally {
      if (this.hasCreateSheetButtonTarget) {
        this.createSheetButtonTarget.textContent = 'Create a new one';
        this.createSheetButtonTarget.disabled = !this.isGoogleConnected();
      }
    }
  }

  selectSpreadsheet(id, name, url = null) {
    this.applySpreadsheetSelection({ id, name, url, clearThread: false });
  }

  async fetchSpreadsheetContext() {
    if (!this.spreadsheetId) return;
    const token = ++this.sheetFetchToken;
    if (this.hasSheetLoadingTarget) this.sheetLoadingTarget.classList.remove('hidden');

    try {
      const url = new URL(this.spreadsheetPreviewUrlValue, window.location.origin);
      url.searchParams.set('spreadsheet_id', this.spreadsheetId);

      const res = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const data = await res.json().catch(() => ({}));
      if (token !== this.sheetFetchToken) return;

      if (!res.ok) {
        const err = new Error(data.error || `Could not read spreadsheet (${res.status})`);
        err.helpUrl = data.help_url;
        err.helpLabel = data.help_label;
        throw err;
      }

      this.spreadsheetName = data.title || this.spreadsheetName;
      this.sheetTabs = (data.sheets || []).map((sheet) => ({
        name: sheet.name,
        headers: sheet.headers || [],
        // A1 letters aligned with headers, so a column can be named the way
        // Apps Script addresses it. Absent on older responses.
        headerColumns: sheet.header_columns || []
      }));
      this.clearSheetError();
      this.renderOnboardingSteps();
      this.renderMentionChips();
      this.renderStatusLine();
      this.renderSheetGraphic();
      if (this.layer === 'context') this.renderContextPanel();
    } catch (error) {
      if (token !== this.sheetFetchToken) return;
      this.showSheetError(error.message || 'Could not read spreadsheet tabs.', error.helpUrl, error.helpLabel);
    } finally {
      if (token === this.sheetFetchToken && this.hasSheetLoadingTarget) {
        this.sheetLoadingTarget.classList.add('hidden');
      }
    }
  }

  scriptInputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.connectScript();
    }
  }

  async connectScript() {
    if (!this.hasScriptInputTarget) return;
    const id = extractScriptId(this.scriptInputTarget.value);
    this.clearScriptError();

    if (!id) {
      this.showScriptError('Paste a valid Apps Script page URL.');
      return;
    }

    if (this.hasConnectScriptButtonTarget) {
      this.connectScriptButtonTarget.disabled = true;
      this.connectScriptButtonTarget.textContent = 'Connecting…';
    }
    if (this.hasScriptLoadingTarget) this.scriptLoadingTarget.classList.remove('hidden');

    try {
      await this.fetchScriptContent(id);
      this.rememberSheetLink(this.spreadsheetId, this.scriptId);
      this.persistConnection(this.spreadsheetId, this.scriptId, {
        spreadsheetName: this.spreadsheetName
      });
      this.step = 'ready';
      this.renderAll();
    } catch (error) {
      this.showScriptError(error.message || 'Could not fetch Apps Script.', error.helpUrl, error.helpLabel);
    } finally {
      if (this.hasConnectScriptButtonTarget) {
        this.connectScriptButtonTarget.disabled = false;
        this.connectScriptButtonTarget.textContent = 'Connect';
      }
      if (this.hasScriptLoadingTarget) this.scriptLoadingTarget.classList.add('hidden');
    }
  }

  get fileLabel() {
    return `${this.targetFile}.gs`;
  }

  // Switching the target re-reads that file from Google, so the thread diffs and
  // pushes against what is actually in it. Turns already on screen keep the file
  // they were written for (turn.file), so nothing lands in the wrong place.
  //
  // Fired by Shadcn::SelectComponent as shadcn--select:change, which carries the
  // value in detail and bubbles up to the wrapper this action sits on.
  async setTargetFile(event) {
    const next = normalizeTargetFile(event.detail?.value ?? event.currentTarget.value);
    if (next === this.targetFile) return;

    this.targetFile = next;
    this.workingCode = null;
    this.otherFiles = [];
    // A different file is a different question: ask again before writing it.
    this.codeWriteConfirmed = false;
    this.clearComposerError();
    this.renderStatusLine();
    this.persistTargetFile(next);

    if (!this.scriptId) return;
    try {
      await this.fetchScriptContent(this.scriptId);
    } catch (error) {
      this.showComposerError(
        error.message || `Could not read ${this.fileLabel}.`,
        error.helpUrl,
        error.helpLabel
      );
    }
  }

  async fetchScriptContent(scriptId, { silent = false } = {}) {
    const token = ++this.scriptFetchToken;
    try {
      const url = new URL(this.scriptPreviewUrlValue, window.location.origin);
      url.searchParams.set('script_id', scriptId);
      url.searchParams.set('file_name', this.targetFile);

      const res = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const data = await res.json().catch(() => ({}));
      if (token !== this.scriptFetchToken) return;

      if (!res.ok) {
        const err = new Error(data.error || `Could not read script (${res.status})`);
        err.helpUrl = data.help_url;
        err.helpLabel = data.help_label;
        throw err;
      }

      this.scriptId = data.script_id || scriptId;
      this.scriptCode = data.code || '';
      this.workingCode = this.scriptCode;
      this.otherFiles = Array.isArray(data.other_files) ? data.other_files : [];
      this.renderStatusLine();
    } catch (error) {
      if (silent) return;
      throw error;
    }
  }

  // ---------- workspace actions ----------

  onPromptInput() {
    this.updateMentionMenu();
    this.syncWorkspaceAppHighlights();
    this.updateSendButton();
  }

  // ---------- Workspace app refs ----------

  // Click toggles the plain app name in the prompt and the icon highlight.
  toggleWorkspaceApp(event) {
    const button = event.currentTarget;
    const label = button?.dataset?.appLabel;
    if (!label || !this.hasPromptInputTarget) return;

    const selected = button.getAttribute('aria-pressed') === 'true';
    if (selected) removeAppRef(this.promptInputTarget, label);
    else insertAppRef(this.promptInputTarget, label);

    this.promptInputTarget.focus();
    this.syncWorkspaceAppHighlights();
    this.updateSendButton();
  }

  // Exact phrase match only: highlights follow what is actually in the prompt.
  syncWorkspaceAppHighlights() {
    if (!this.hasWorkspaceAppTarget) return;

    const text = this.hasPromptInputTarget ? this.promptInputTarget.value : '';
    const present = appsPresentInText(text);

    for (const button of this.workspaceAppTargets) {
      const on = present.has(button.dataset.appId);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  // ---------- confirmation accent ----------

  // The colour recognition speaks in. One CSS variable feeds every tint (see
  // _automate.scss), so a click here repaints the rings, the sheet graphic, the
  // state switch and AND THEN together.
  loadAccent() {
    try {
      const saved = window.localStorage?.getItem(ACCENT_STORAGE_KEY);
      if (saved && ACCENTS.includes(saved)) return saved;
    } catch {
      // Private browsing and blocked storage both land here; the default is fine.
    }
    return ACCENTS[0];
  }

  setAccent(event) {
    const accent = event.currentTarget.dataset.accent;
    if (!accent || !ACCENTS.includes(accent)) return;
    this.accent = accent;
    try {
      window.localStorage?.setItem(ACCENT_STORAGE_KEY, accent);
    } catch {
      // Not worth failing a colour change over.
    }
    this.renderAccent();
  }

  renderAccent() {
    this.element.style.setProperty('--automate-accent', this.accent);
    if (!this.hasAccentSwatchTarget) return;
    for (const swatch of this.accentSwatchTargets) {
      const on = swatch.dataset.accent === this.accent;
      swatch.setAttribute('aria-pressed', String(on));
      swatch.style.borderColor = on ? '#e2e8f0' : 'transparent';
      swatch.style.boxShadow = on ? '0 0 0 2px rgba(226,232,240,0.25)' : 'none';
    }
  }

  // ---------- entry state ----------

  setUiMode(event) {
    const mode = event.currentTarget.dataset.uiMode;
    if (!mode || mode === this.uiMode) return;
    this.uiMode = mode;
    this.updateBlankState();
    this.renderUiMode();
    if (mode === 'know' && this.hasPromptInputTarget) this.promptInputTarget.focus();
  }

  renderUiMode() {
    const explore = this.uiMode === 'explore';
    if (this.hasStateSwitchTarget) this.stateSwitchTarget.classList.remove('hidden');
    if (this.hasStateSwitchButtonTarget) {
      for (const button of this.stateSwitchButtonTargets) {
        const on = button.dataset.uiMode === this.uiMode;
        button.className = on ? STATE_SWITCH_ON : STATE_SWITCH_OFF;
        button.setAttribute('aria-pressed', String(on));
      }
    }
    if (this.hasKnowStateTarget) this.knowStateTarget.classList.toggle('hidden', explore);
    if (this.hasExploreStateTarget) this.exploreStateTarget.classList.toggle('hidden', !explore);
    if (explore && this.hasBuilderGroupsTarget && !this.builderGroupsTarget.innerHTML) {
      this.builderGroupsTarget.innerHTML = builderGroupsHtml();
    }
    this.renderSheetGraphic();
    this.renderBuilderChain();
  }

  // ---------- sheet graphic ----------

  // Both hold names from the sheet that just went away.
  resetSheetGraphicState() {
    this.graphicTab = null;
    this.excludedContext = new Set();
    this.builder = { trigger: null, current: [], steps: [], text: '' };
    this.draggingTile = null;
  }

  // What a click on the graphic writes into the prompt. A bare word disappears
  // into a sentence — "Total" could be anything — so the part says what it is:
  // "Tab: Orders", "Column: Total (C)". The letter comes from row 1's real
  // position and is left off when the sheet did not give us one.
  sheetPartToken(kind, label, column = null) {
    if (kind === 'tab') return `Tab: ${label}`;
    if (kind === 'header') return column ? `Column: ${label} (${column})` : `Column: ${label}`;
    return label;
  }

  // Both forms count as naming the part: the qualified token a click writes,
  // and the bare name someone typing would use.
  sheetPartTerms(kind, label, column = null) {
    const token = this.sheetPartToken(kind, label, column);
    return token === label ? [label] : [token, label];
  }

  // Which tab the graphic shows: the first one the prompt names, else the last
  // one clicked, else the first tab of the spreadsheet.
  shownTabIndex(text) {
    const tabs = this.sheetTabs || [];
    if (!tabs.length) return -1;
    const named = tabs.findIndex((tab) => textNamesTerm(text, this.sheetPartTerms('tab', tab.name)));
    if (named !== -1) return named;
    const clicked = tabs.findIndex((tab) => tab.name === this.graphicTab);
    return clicked !== -1 ? clicked : 0;
  }

  isExcluded(key) {
    return this.excludedContext.has(key);
  }

  tabExclusionKey(tabName) {
    return tabKey(this.spreadsheetId, this.spreadsheetName, tabName);
  }

  // Recognition is derived, never stored: every keystroke re-reads the prompt.
  // Nothing named yet means nothing is singled out, so the whole graphic sits
  // neutral rather than uniformly dim.
  buildGraphicModel(mode) {
    const text = mode === 'explore' || !this.hasPromptInputTarget ? '' : this.promptInputTarget.value;
    const tabs = this.sheetTabs || [];
    const fileName = this.spreadsheetName || 'Your spreadsheet';
    const activeIndex = this.shownTabIndex(text);
    const activeTab = activeIndex === -1 ? null : tabs[activeIndex];

    const allHeaders = activeTab?.headers || [];
    const shownHeaders = allHeaders.slice(0, MAX_GRAPHIC_HEADERS);
    const hiddenHeaders = allHeaders.length - shownHeaders.length;

    const headerColumn = (index) => (activeTab?.headerColumns || [])[index] || null;

    const fileNamed = textNamesTerm(text, fileName);
    const tabNamed = tabs.map((tab) => textNamesTerm(text, this.sheetPartTerms('tab', tab.name)));
    const headerNamed = shownHeaders.map((header, index) =>
      textNamesTerm(text, this.sheetPartTerms('header', header, headerColumn(index)))
    );
    const anyNamed = fileNamed || tabNamed.some(Boolean) || headerNamed.some(Boolean);

    const state = (named) => (named ? 'on' : anyNamed ? 'dim' : 'neutral');

    const model = {
      mode,
      file: {
        label: fileName,
        key: 'file',
        state: state(fileNamed),
        excluded: this.isExcluded('file')
      },
      headers: shownHeaders.map((header, index) => ({
        label: header,
        token: this.sheetPartToken('header', header, headerColumn(index)),
        key: `header:${header}`,
        state: state(headerNamed[index])
      })),
      tabs: tabs.map((tab, index) => ({
        label: tab.name,
        token: this.sheetPartToken('tab', tab.name),
        key: this.tabExclusionKey(tab.name),
        state: index === activeIndex && !anyNamed ? 'on' : state(tabNamed[index]),
        excluded: this.isExcluded(this.tabExclusionKey(tab.name))
      })),
      tabHint:
        mode === 'explore'
          ? 'Use any part: file, tab, column, row, or a cell'
          : hiddenHeaders > 0
            ? `+${hiddenHeaders} more columns`
            : ''
    };

    if (mode === 'explore') {
      model.rows = GRAPHIC_ROWS.map((row) => ({ label: row, key: `row:${row}`, state: 'neutral' }));
      model.cellRows = GRAPHIC_ROWS.map((row) =>
        GRAPHIC_COLUMNS.map((column) => ({
          label: `${column}${row}`,
          key: `cell:${column}${row}`,
          state: 'neutral'
        }))
      );
    }

    return model;
  }

  renderSheetGraphic() {
    if (!this.hasSheetGraphicTarget) return;
    // Standalone has a placeholder name but no sheet: nothing to draw.
    const hasSheet = !this.standalone && ((this.sheetTabs || []).length > 0 || Boolean(this.spreadsheetName));
    for (const container of this.sheetGraphicTargets) {
      const mode = container.dataset.graphicMode === 'explore' ? 'explore' : 'know';
      container.classList.toggle('hidden', !hasSheet);
      if (!hasSheet) {
        container.innerHTML = '';
        continue;
      }
      container.innerHTML = sheetGraphicHtml(this.buildGraphicModel(mode));
    }
  }

  // Same toggle contract as the app icons: click puts the name in the prompt,
  // click again takes the nearest one back out. In the explore state the same
  // parts are tiles, so a click drops one into the open step instead.
  toggleSheetPart(event) {
    const button = event.currentTarget;
    const label = button.dataset.partLabel;
    const kind = button.dataset.partKind;
    const token = button.dataset.partToken || label;
    if (!label) return;

    if (kind === 'tab') this.graphicTab = label;

    if (this.uiMode === 'explore') {
      this.pushBuilderTile(kind, label, token);
      this.renderSheetGraphic();
      return;
    }

    if (!this.hasPromptInputTarget) return;
    if (button.getAttribute('aria-pressed') === 'true') {
      // Take back the qualified form first; fall back to the bare name, which
      // is what a hand-typed mention of the same tab or column looks like.
      if (!removeTerm(this.promptInputTarget, [token])) removeTerm(this.promptInputTarget, [label]);
    } else {
      insertTerm(this.promptInputTarget, token);
    }

    this.promptInputTarget.focus();
    this.updateSendButton();
  }

  // Right-click keeps a file or tab out of the context prefix without touching
  // the prompt: dashed and struck through means "do not send this".
  toggleSheetPartExcluded(event) {
    const button = event.currentTarget;
    const kind = button.dataset.partKind;
    if (kind !== 'file' && kind !== 'tab') return;
    event.preventDefault();

    const key = kind === 'file' ? 'file' : this.tabExclusionKey(button.dataset.partLabel);
    if (this.excludedContext.has(key)) this.excludedContext.delete(key);
    else this.excludedContext.add(key);
    this.renderSheetGraphic();
  }

  // ---------- explore builder ----------

  // label is what the tile reads; token is what it contributes to the prompt.
  pushBuilderTile(kind, label, token = null) {
    if (!label) return;
    this.builder.current.push({ kind: kind || 'action', label, token: token || label });
    // A placeholder value is only useful once it says your value, so land the
    // caret in it with the text selected, ready to be typed over.
    this.focusTileIndex = EDITABLE_KINDS.includes(kind) ? this.builder.current.length - 1 : null;
    this.renderBuilderChain();
  }

  addBuilderTile(event) {
    const button = event.currentTarget;
    this.pushBuilderTile(button.dataset.partKind, button.dataset.partLabel, button.dataset.partToken);
  }

  tileDragStart(event) {
    const button = event.currentTarget;
    this.draggingTile = {
      kind: button.dataset.partKind || 'action',
      label: button.dataset.partLabel || '',
      token: button.dataset.partToken || button.dataset.partLabel || ''
    };
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('text/plain', this.draggingTile.label);
    }
    this.renderBuilderChain();
  }

  tileDragEnd() {
    this.draggingTile = null;
    this.renderBuilderChain();
  }

  tileDragOver(event) {
    if (!this.draggingTile) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  tileDropOnCurrent(event) {
    if (!this.draggingTile) return;
    event.preventDefault();
    const tile = this.draggingTile;
    this.draggingTile = null;
    this.pushBuilderTile(tile.kind, tile.label, tile.token);
  }

  // Locked steps stay editable: a tile can be dropped into one after the fact.
  tileDropOnStep(event) {
    if (!this.draggingTile) return;
    event.preventDefault();
    const index = Number(event.currentTarget.dataset.stepIndex);
    const tile = this.draggingTile;
    this.draggingTile = null;
    if (!this.builder.steps[index]) return;
    this.builder.steps[index].push({ kind: tile.kind, label: tile.label, token: tile.token });
    this.renderBuilderChain();
  }

  // The trigger is a single choice, and clicking it again clears it — there is
  // no "no trigger" pill to hunt for.
  setBuilderTrigger(event) {
    const button = event.currentTarget;
    const id = button.dataset.triggerId;
    if (!id) return;
    // Switching triggers drops the old trigger's "what exactly" with it.
    this.builder.trigger =
      this.builder.trigger?.id === id ? null : { id, token: button.dataset.triggerToken || '', option: null };
    this.renderBuilderState();
  }

  // Optional narrowing of the chosen trigger; clicking it again goes back to
  // the broad version.
  setBuilderTriggerOption(event) {
    const button = event.currentTarget;
    const trigger = this.builder.trigger;
    if (!trigger || trigger.id !== button.dataset.triggerId) return;

    const id = button.dataset.optionId;
    trigger.option =
      trigger.option?.id === id ? null : { id, token: button.dataset.optionToken || '' };
    this.renderBuilderState();
  }

  renderBuilderTriggers() {
    if (!this.hasBuilderTriggerTarget) return;
    const current = this.builder.trigger;

    for (const button of this.builderTriggerTargets) {
      const on = current?.id === button.dataset.triggerId;
      button.className = on ? TRIGGER_PILL_ON : TRIGGER_PILL_OFF;
      button.setAttribute('aria-pressed', String(on));
    }

    if (this.hasBuilderTriggerOptionsTarget) {
      for (const group of this.builderTriggerOptionsTargets) {
        group.classList.toggle('hidden', group.dataset.triggerId !== current?.id);
      }
    }

    if (this.hasBuilderTriggerOptionTarget) {
      for (const button of this.builderTriggerOptionTargets) {
        const on = current?.id === button.dataset.triggerId && current?.option?.id === button.dataset.optionId;
        button.className = on ? TRIGGER_OPTION_ON : TRIGGER_OPTION_OFF;
        button.setAttribute('aria-pressed', String(on));
      }
    }
  }

  // Typing does not re-render the chain: that would drop the caret on every
  // keystroke. Only the preview and the two buttons need updating.
  updateBuilderText(event) {
    this.builder.text = event.currentTarget.value;
    this.renderBuilderState();
  }

  builderTextKeydown(event) {
    const input = event.currentTarget;
    const empty = !input.value.length;
    const atStart = (input.selectionStart ?? 0) === 0 && (input.selectionEnd ?? 0) === 0;

    if (event.key === 'Enter') {
      event.preventDefault();
      // Enter banks what was typed as its own tile; on an empty caret it does
      // what AND THEN does, so the whole chain can be built without the mouse.
      if (input.value.trim()) this.commitBuilderText();
      else this.commitBuilderStep();
      return;
    }

    if ((event.key === 'Backspace' && (empty || atStart)) || (event.key === 'Delete' && empty)) {
      if (!this.builder.current.length) return;
      event.preventDefault();
      this.builder.current.pop();
      this.renderBuilderChain();
      // The box is rebuilt around a new input; keep typing where you were.
      this.focusBuilderInput();
    }
  }

  // Turns the typed fragment into a tile of its own.
  commitBuilderText() {
    const text = this.builder.text.trim();
    if (!text) return false;
    this.builder.text = '';
    this.builder.current.push({ kind: 'text', label: text, token: text });
    this.renderBuilderChain();
    return true;
  }

  // Clicking the empty part of the box puts the caret at the end. Clicking
  // anything that handles its own focus — a tile's × , or an editable value or
  // text tile — must be left alone, or the caret jumps away the instant you try
  // to fix a typo in a tile.
  focusBuilderText(event) {
    if (event?.target?.closest('button, input, textarea, select')) return;
    this.focusBuilderInput();
  }

  // The open box's tile inputs, addressed by position in the open step.
  focusBuilderTileInput(tileIndex) {
    if (!this.hasBuilderTileInputTarget) return;
    const input = this.builderTileInputTargets.find(
      (candidate) =>
        Number(candidate.dataset.stepIndex) === -1 && Number(candidate.dataset.tileIndex) === tileIndex
    );
    if (!input) return;
    input.focus();
    input.select();
  }

  focusBuilderInput() {
    if (!this.hasBuilderTextTarget) return;
    const input = this.builderTextTarget;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }

  // Values arrive as placeholders — "Pending", "name@company.com" — so they are
  // inputs once they are in the chain. Editing does not re-render: that would
  // take the caret away mid-word.
  editBuilderTile(event) {
    const input = event.currentTarget;
    const tile = this.builderTileAt(input.dataset.stepIndex, input.dataset.tileIndex);
    if (!tile) return;
    tile.label = input.value;
    tile.token = input.value;
    input.size = Math.max(input.value.length, 3);
    this.renderBuilderState();
  }

  builderTileKeydown(event) {
    const input = event.currentTarget;
    if (event.key === 'Enter') {
      event.preventDefault();
      // Done with this tile: back to the caret at the end of the box.
      this.focusBuilderInput();
      return;
    }
    // Backspace on an already-empty value takes the tile out, like any chip.
    if (event.key === 'Backspace' && !input.value.length) {
      event.preventDefault();
      const tile = this.builderTileAt(input.dataset.stepIndex, input.dataset.tileIndex);
      if (!tile) return;
      this.removeBuilderTileAt(Number(input.dataset.stepIndex), Number(input.dataset.tileIndex));
      this.focusBuilderInput();
    }
  }

  builderTileAt(stepIndex, tileIndex) {
    const tiles = Number(stepIndex) === -1 ? this.builder.current : this.builder.steps[Number(stepIndex)];
    return tiles?.[Number(tileIndex)] || null;
  }

  removeBuilderTileAt(stepIndex, tileIndex) {
    const tiles = stepIndex === -1 ? this.builder.current : this.builder.steps[stepIndex];
    if (!tiles) return;
    tiles.splice(tileIndex, 1);
    if (stepIndex !== -1 && !tiles.length) this.builder.steps.splice(stepIndex, 1);
    this.renderBuilderChain();
  }

  removeBuilderTile(event) {
    this.removeBuilderTileAt(
      Number(event.currentTarget.dataset.stepIndex),
      Number(event.currentTarget.dataset.tileIndex)
    );
  }

  removeBuilderStep(event) {
    const index = Number(event.currentTarget.dataset.stepIndex);
    this.builder.steps.splice(index, 1);
    this.renderBuilderChain();
  }

  // AND THEN: commit the open box to the chain and open a fresh one. Anything
  // typed but not yet banked goes in with it rather than being lost.
  commitBuilderStep() {
    const text = this.builder.text.trim();
    if (text) {
      this.builder.text = '';
      this.builder.current.push({ kind: 'text', label: text, token: text });
    }
    if (!this.builder.current.length) return;
    this.builder.steps.push(this.builder.current);
    this.builder.current = [];
    this.renderBuilderChain();
  }

  resetBuilder() {
    this.builder = { trigger: null, current: [], steps: [], text: '' };
    this.draggingTile = null;
    this.renderBuilderChain();
  }

  renderBuilderChain() {
    if (!this.hasBuilderChainTarget) return;
    // The box is rebuilt wholesale, so remember whether the caret was in it.
    const hadFocus = this.hasBuilderTextTarget && document.activeElement === this.builderTextTarget;

    this.builderChainTarget.innerHTML = builderChainHtml({
      steps: this.builder.steps,
      current: this.builder.current,
      text: this.builder.text,
      dragging: Boolean(this.draggingTile)
    });

    if (this.focusTileIndex !== null && this.focusTileIndex !== undefined) {
      this.focusBuilderTileInput(this.focusTileIndex);
      this.focusTileIndex = null;
    } else if (hadFocus) {
      this.focusBuilderInput();
    }
    this.renderBuilderState();
  }

  // Preview line and the button row, without touching the chain markup.
  renderBuilderState() {
    this.renderBuilderTriggers();
    const prompt = this.builderPromptText();
    if (this.hasBuilderAndThenTarget) {
      // AND THEN banks the open box, so it needs something in the box — typed
      // or tiled — rather than a whole chain.
      const canCommit = this.builder.current.length > 0 || this.builder.text.trim().length > 0;
      this.builderAndThenTarget.disabled = !canCommit;
      this.builderAndThenTarget.className = canCommit ? BUILDER_AND_THEN_ENABLED : BUILDER_AND_THEN_DISABLED;
    }
    if (this.hasBuilderPreviewTarget) {
      this.builderPreviewTarget.textContent = prompt ? `Sends: ${prompt}` : '';
    }
    if (this.hasBuilderGenerateTarget) {
      const enabled = Boolean(prompt) && this.phase !== 'writing' && this.agentConfiguredValue;
      this.builderGenerateTarget.disabled = !enabled;
      this.builderGenerateTarget.className = enabled ? BUILDER_GENERATE_ENABLED : BUILDER_GENERATE_DISABLED;
    }
  }

  // The agent contract is prompt text, so the chain flattens to a sentence
  // rather than to structured JSON.
  builderPromptText() {
    const trigger = this.builder.trigger;
    const lead = trigger?.token
      ? `${trigger.token}${trigger.option?.token ? ` — ${trigger.option.token}` : ''}: `
      : '';
    const pending = this.builder.text.trim();
    const open = pending
      ? [...this.builder.current, { kind: 'text', label: pending, token: pending }]
      : this.builder.current;
    const steps = [...this.builder.steps, open].filter((tiles) => tiles.length);
    if (!steps.length) return '';
    const body = steps
      .map((tiles) => tiles.map((tile) => tile.token || tile.label).join(' '))
      .join(' and then ');
    return `${lead}${body}`;
  }

  generateFromBuilder() {
    const prompt = this.builderPromptText();
    if (!prompt || !this.hasPromptInputTarget) return;

    this.promptInputTarget.value = prompt;
    // Back to the know state so the answer lands where the thread already is.
    this.uiMode = 'know';
    this.renderUiMode();
    this.updateSendButton();
    this.generate();
  }

  promptKeydown(event) {
    if (this.mentionMenuOpen) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          return this.moveMentionSelection(1);
        case 'ArrowUp':
          event.preventDefault();
          return this.moveMentionSelection(-1);
        case 'Tab':
          event.preventDefault();
          return this.chooseMention(this.mentionIndex);
        case 'Enter':
          // ⌘↵ still sends: someone who typed the whole tab name out should not
          // have to dismiss the menu first.
          if (event.metaKey || event.ctrlKey) break;
          event.preventDefault();
          return this.chooseMention(this.mentionIndex);
        case 'Escape':
          // Stops the document-level handler from closing an open panel too.
          event.preventDefault();
          event.stopPropagation();
          return this.closeMentionMenu();
        default:
          break;
      }
    }

    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.closeMentionMenu();
      this.send();
    }
  }

  // Arrow keys and clicks move the caret without firing input, so the menu has
  // to re-read where it now sits.
  promptCaretMoved(event) {
    if (event?.type === 'click' || ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event?.key)) {
      this.updateMentionMenu();
    }
  }

  useSuggestion(event) {
    const prompt = event.currentTarget.dataset.prompt;
    if (!prompt || !this.hasPromptInputTarget) return;
    this.promptInputTarget.value = prompt;
    this.promptInputTarget.focus();
    this.syncWorkspaceAppHighlights();
    this.updateSendButton();
  }

  updateSendButton() {
    this.resizePrompt();
    this.syncWorkspaceAppHighlights();
    // Recognition follows the prompt from wherever it changed: typing, a
    // suggestion chip, an icon click, a click on the graphic itself.
    this.renderSheetGraphic();
    // Keeps the builder's Generate in step with phase / agent availability.
    if (this.uiMode === 'explore') this.renderBuilderChain();
    if (!this.hasSendButtonTarget) return;
    const hasText = this.hasPromptInputTarget && this.promptInputTarget.value.trim().length > 0;
    const canRunEmpty = Boolean(PROSE_DEFAULT_PROMPT[this.mode]);
    const enabled = (hasText || canRunEmpty) && this.phase !== 'writing' && this.agentConfiguredValue;
    this.sendButtonTarget.disabled = !enabled;
    this.sendButtonTarget.innerHTML =
      this.phase === 'writing' ? 'Writing…' : `<span>Send</span>${SEND_SHORTCUT}`;
    this.sendButtonTarget.className = enabled ? SEND_ENABLED : SEND_DISABLED;
    const safeSend = enabled && SAFE_MODES.includes(this.mode);
    this.sendButtonTarget.style.backgroundColor = safeSend ? '#f59e0b' : '';
    this.sendButtonTarget.style.color = safeSend ? '#451a03' : '';
    this.applySafeModeChrome();
  }

  // Grow past the default 2 rows when the prompt wraps, capped at ~5 lines.
  resizePrompt() {
    if (!this.hasPromptInputTarget) return;
    const el = this.promptInputTarget;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 136)}px`;
  }

  send() {
    if (this.hasSendButtonTarget && this.sendButtonTarget.disabled) return;
    this.closeMentionMenu();
    this.generate();
  }

  stopGeneration() {
    if (this.streamController) this.streamController.abort();
    this.phase = 'idle';
    this.renderStreaming();
    this.updateSendButton();
  }

  setMode(event) {
    const mode = event.currentTarget.dataset.mode;
    if (!mode) return;
    this.mode = mode;
    this.renderModePills();
    this.updateSendButton();
  }

  reset() {
    if (this.streamController) this.streamController.abort();
    const wasStandalone = this.standalone;
    this.step = this.isGoogleConnected() ? 'sheet' : 'connect';
    this.layer = null;
    this.mode = 'write';

    this.spreadsheetId = null;
    this.spreadsheetName = null;
    this.spreadsheetUrl = null;
    this.sheetTabs = [];
    this.extraSpreadsheets = [];
    this.resetSheetGraphicState();
    this.codeWriteConfirmed = false;

    this.scriptId = null;
    this.scriptCode = null;
    this.workingCode = null;
    this.otherFiles = [];

    this.turns = [];
    this.phase = 'idle';

    if (this.hasScriptInputTarget) this.scriptInputTarget.value = '';
    if (this.hasPromptInputTarget) this.promptInputTarget.value = '';

    this.closeMentionMenu();
    this.clearSheetError();
    this.clearScriptError();
    this.clearComposerError();
    // Clear the active sheet so a refresh does not jump back into the old one.
    // Per-sheet script links stay in the DB for the next time they pick it.
    this.persistConnection(null, null);
    this.panelTurnIndex = null;
    this.panelTab = 'code';
    // Start over inside standalone clears the conversation, not the choice.
    if (wasStandalone) this.enterStandalone();
    this.renderAll();
  }

  // Re-reads tabs/headers and the target file from Google, so edits made in the
  // spreadsheet or the script editor show up without reloading the page.
  async refreshContext() {
    if (this.refreshing || this.standalone) return;
    this.refreshing = true;

    if (this.hasRefreshButtonTarget) this.refreshButtonTarget.disabled = true;
    if (this.hasRefreshLabelTarget) this.refreshLabelTarget.textContent = 'Refreshing…';

    try {
      await Promise.all([
        this.spreadsheetId ? this.fetchSpreadsheetContext() : Promise.resolve(),
        this.scriptId ? this.fetchScriptContent(this.scriptId, { silent: true }) : Promise.resolve()
      ]);

      this.renderStatusLine();
      this.renderMentionChips();
      if (this.layer === 'context') this.renderContextPanel();

      if (this.hasRefreshLabelTarget) {
        this.refreshLabelTarget.textContent = 'Up to date';
        setTimeout(() => {
          if (this.hasRefreshLabelTarget) this.refreshLabelTarget.textContent = 'Refresh';
        }, 1600);
      }
    } catch (error) {
      this.showComposerError(error.message || 'Could not refresh from Google.');
      if (this.hasRefreshLabelTarget) this.refreshLabelTarget.textContent = 'Refresh';
    } finally {
      this.refreshing = false;
      if (this.hasRefreshButtonTarget) this.refreshButtonTarget.disabled = false;
    }
  }

  // Persist which sheet/script is active so a reload restores the right one
  // (and a cleared sheet stays cleared).
  persistConnection(spreadsheetId, scriptId, { spreadsheetName = null } = {}) {
    const body = { spreadsheet_id: spreadsheetId, script_id: scriptId };
    if (spreadsheetName) body.spreadsheet_name = spreadsheetName;
    return this.patchConnection(body);
  }

  // Sent on its own: a null id means "clear it", so the file choice must never
  // ride along with ids the caller did not mean to change.
  persistTargetFile(targetFile) {
    return this.patchConnection({ target_file: targetFile });
  }

  async patchConnection(body) {
    if (!this.hasConnectionUrlValue) return null;
    try {
      const res = await fetch(this.connectionUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      if (data.sheet_links) {
        this.sheetLinksMap = this.buildSheetLinksMap(data.sheet_links);
      }
      return data;
    } catch {
      // Non-fatal: the page still works, the choice just will not survive reload.
      return null;
    }
  }

  // Disconnects the active sheet and returns to the picker. The thread is
  // cleared too: turns hold code diffed against the old script, so pushing one
  // into a different sheet would be wrong.
  clearSheet() {
    this.spreadsheetId = null;
    this.spreadsheetName = null;
    this.spreadsheetUrl = null;
    this.sheetTabs = [];
    this.resetSheetGraphicState();
    this.codeWriteConfirmed = false;
    this.scriptId = null;
    this.scriptCode = null;
    this.workingCode = null;
    this.otherFiles = [];
    this.turns = [];

    if (this.hasScriptInputTarget) this.scriptInputTarget.value = '';
    if (this.hasPromptInputTarget) this.promptInputTarget.value = '';

    this.persistConnection(null, null);
    this.layer = null;
    this.step = 'sheet';
    this.clearSheetError();
    this.clearScriptError();
    this.clearComposerError();
    this.renderTurns();
    this.renderAll();
  }

  // Promotes a spreadsheet already in the context list to be the one we write to.
  useSpreadsheet(event) {
    const id = event.currentTarget.dataset.spreadsheetId;
    if (!id || id === this.spreadsheetId) return;

    const index = this.extraSpreadsheets.findIndex((sheet) => sheet.id === id);
    if (index === -1) return;
    const promoted = this.extraSpreadsheets.splice(index, 1)[0];

    // The previously active sheet stays available as read-only context.
    if (this.spreadsheetId) {
      this.extraSpreadsheets.unshift({
        id: this.spreadsheetId,
        name: this.spreadsheetName,
        tabs: this.sheetTabs
      });
    }

    this.layer = null;
    this.applySpreadsheetSelection({
      id: promoted.id,
      name: promoted.name,
      url: `https://docs.google.com/spreadsheets/d/${promoted.id}/edit`,
      clearThread: true
    });
    this.renderTurns();
  }

  removeSpreadsheet(event) {
    const id = event.currentTarget.dataset.spreadsheetId;
    this.extraSpreadsheets = this.extraSpreadsheets.filter((sheet) => sheet.id !== id);
    this.renderContextPanel();
  }

  // ---------- streaming / generation ----------

  // Kept under a hard budget: this prefix rides on every prompt, and the agent
  // endpoint rejects anything over 8,000 characters. Two wide spreadsheets used
  // to blow that on their own, failing the run before the user typed anything.
  // A prompt that @mentions tabs is saying where the work happens, so those tabs
  // get their full header row and the rest collapse to bare names. That points
  // the agent at the right tab and buys back budget on sheets wide enough that
  // headers alone used to fill it.
  buildContextPrefix(promptText = '') {
    // Parts switched off on the sheet graphic are left out entirely: excluding
    // the file drops the primary spreadsheet, excluding a tab drops that tab.
    const primaryExcluded = this.isExcluded('file');
    const sheets = [
      ...(primaryExcluded
        ? []
        : [
            {
              id: this.spreadsheetId,
              name: this.spreadsheetName,
              tabs: (this.sheetTabs || []).filter((tab) => !this.isExcluded(this.tabExclusionKey(tab.name))),
              primary: true
            }
          ]),
      ...this.extraSpreadsheets
    ].filter((sheet) => sheet.tabs && sheet.tabs.length);
    if (!sheets.length) return '';

    const mentions = findMentions(promptText, this.mentionables);
    const mentionKeys = new Set(
      mentions.map((mention) => tabKey(mention.sheetId, mention.sheetName, mention.tabName))
    );
    const hasMentions = mentions.length > 0;

    const describeTab = (tab, mentioned) => {
      if (hasMentions && !mentioned) return tab.name;

      const headers = tab.headers || [];
      const limit = mentioned ? MAX_HEADERS_PER_MENTIONED_TAB : MAX_HEADERS_PER_TAB;
      const shown = headers.slice(0, limit);
      const hidden = headers.length - shown.length;
      const cols = shown.length
        ? `${shown.join(' · ')}${hidden > 0 ? ` +${hidden} more` : ''}`
        : 'no headers';
      return `${tab.name} (${cols})`;
    };

    const described = sheets.map((sheet) => ({
      name: sheet.name || 'Spreadsheet',
      tabs: (sheet.tabs || []).map((tab) => {
        const mentioned = mentionKeys.has(tabKey(sheet.id, sheet.name, tab.name));
        return { text: describeTab(tab, mentioned), mentioned };
      })
    }));

    const parts = [];
    // Mentioned tabs are the point of the prompt, so they are charged against
    // the budget up front and never dropped.
    let used = described.reduce(
      (total, sheet) =>
        total + sheet.tabs.filter((tab) => tab.mentioned).reduce((sum, tab) => sum + tab.text.length, 0),
      0
    );
    let omitted = 0;

    for (const sheet of described) {
      const tabs = [];
      for (const tab of sheet.tabs) {
        if (tab.mentioned) {
          tabs.push(tab.text);
          continue;
        }
        if (used + tab.text.length > MAX_CONTEXT_CHARS) {
          omitted += 1;
          continue;
        }
        used += tab.text.length;
        tabs.push(tab.text);
      }
      if (tabs.length) parts.push(`"${sheet.name}" — tabs: ${tabs.join(', ')}`);
    }

    if (!parts.length) return '';

    const note = omitted ? ` (${omitted} further tab${omitted === 1 ? '' : 's'} not listed)` : '';
    const lines = [`Available spreadsheet context: ${parts.join('; ')}.${note}`];

    if (hasMentions) {
      const named = mentions.map((mention) =>
        mention.primary ? `"${mention.tabName}"` : `"${mention.tabName}" in "${mention.sheetName}"`
      );
      const one = mentions.length === 1;
      lines.push(
        `The prompt names ${named.join(', ')} with @ — ${one ? 'that is the tab' : 'those are the tabs'} to work ` +
          `against. Columns are listed above only for the named ${one ? 'tab' : 'tabs'}.`
      );
    }

    return `${lines.join('\n')}\n\n`;
  }

  streamLabelForPhase(phase) {
    const sheetName = this.standalone ? 'your request' : this.spreadsheetName || 'your spreadsheet';
    const readCode = this.sentExistingCode ? `Reading ${this.fileLabel}` : `Reading ${sheetName}`;

    const PROSE_LABELS = {
      comment: [`Reading ${this.fileLabel}`, 'Working through each function', 'Writing comments'],
      plan: [`Reading ${sheetName}`, readCode, 'Looking for what is missing', 'Writing the plan'],
      map: [`Reading ${sheetName}`, readCode, 'Tracing what calls what', 'Writing the map'],
      ask: [`Reading ${sheetName}`, readCode, 'Finding the answer', 'Writing it up'],
      fix: [`Reading ${sheetName}`, readCode, 'Looking for what could break', 'Working out the fix']
    };

    if (this.mode !== 'write') {
      const labels = PROSE_LABELS[this.mode] || [];
      return labels[Math.min(phase, labels.length - 1)] || 'Working';
    }

    switch (phase) {
      case 0:
        return `Reading ${sheetName}`;
      case 1:
        return this.sentExistingCode ? `Reading ${this.fileLabel}` : 'Planning the change';
      case 2:
        return 'Planning the change';
      case 3:
        return this.streamFnName ? `Writing ${this.streamFnName}()` : 'Writing code';
      default:
        return 'Working';
    }
  }

  streamHeaderLabel() {
    if (this.mode === 'fix') return 'Debugging';
    if (this.isCodeMode()) return 'Writing code';
    return { plan: 'Planning', map: 'Mapping', ask: 'Answering', fix: 'Debugging' }[this.mode] || 'Working';
  }

  advanceStreamPhase(next) {
    if (next <= this.streamPhase) return;
    this.streamLog.push(this.streamLabelForPhase(this.streamPhase));
    this.streamPhase = next;
    this.renderStreaming();
  }

  handleStreamEvent(event) {
    if (this.streamPhase === 0 && ['delta', 'thinking', 'tool', 'agent'].includes(event.type)) {
      this.advanceStreamPhase(1);
    }

    if (event.type === 'delta' || event.type === 'thinking') {
      this.streamBuffer += event.text || '';

      if (this.streamPhase === 1 && this.streamBuffer.length > 120) {
        this.advanceStreamPhase(2);
      }

      if (this.isCodeMode()) {
        if (this.streamPhase < 3) {
          const match = this.streamBuffer.match(/function\s+([A-Za-z_$][\w$]*)\s*\(/);
          if (match) {
            this.streamFnName = match[1];
            this.advanceStreamPhase(3);
          }
        }
      } else if (this.streamPhase < 3 && this.streamBuffer.length > 400) {
        this.advanceStreamPhase(3);
      }
    }
  }

  renderStreaming() {
    const writing = this.phase === 'writing';
    if (this.hasStreamingCardTarget) this.streamingCardTarget.classList.toggle('hidden', !writing);
    if (!writing) return;

    if (this.hasStreamLogTarget) {
      this.streamLogTarget.innerHTML = this.streamLog
        .map(
          (line) => `
            <div class="flex gap-[9px] text-[15.5px] leading-[1.5] text-[#94a3b8]"><span class="text-[#34d399]">✓</span>${this.escapeHtml(line)}</div>
          `
        )
        .join('');
    }
    if (this.hasStreamActiveLabelTarget) {
      this.streamActiveLabelTarget.textContent = this.streamLabelForPhase(this.streamPhase);
    }
    if (this.hasStreamHeaderTarget) {
      this.streamHeaderTarget.textContent = this.streamHeaderLabel();
    }
  }

  async generate() {
    this.clearComposerError();
    const typed = this.hasPromptInputTarget ? this.promptInputTarget.value.trim() : '';
    const promptRaw = typed || PROSE_DEFAULT_PROMPT[this.mode] || '';
    if (!promptRaw || this.phase === 'writing' || !this.agentConfiguredValue) return;

    // A silent read failure used to leave workingCode null, and a run against
    // "no existing code" produces a file that replaces everything already
    // there. Read it now, and refuse the run rather than write blind.
    if (this.scriptId && this.workingCode == null) {
      try {
        await this.fetchScriptContent(this.scriptId);
      } catch {
        // fall through to the check below
      }
      if (this.workingCode == null) {
        this.showComposerError(
          `Automate could not read ${this.fileLabel}. Press Refresh, then try again — running now would write over whatever is in it.`
        );
        return;
      }
    }

    const existingCode = this.workingCode || '';
    // Mode behaviour lives in the server-side system prompt; the client only supplies
    // the sheet context the agent cannot see.
    const fullPrompt = `${this.buildContextPrefix(promptRaw)}${promptRaw}`;
    const modeAtSend = this.mode;

    this.phase = 'writing';
    this.streamLog = [];
    this.streamPhase = 0;
    this.streamFnName = null;
    this.streamBuffer = '';
    this.sentExistingCode = Boolean(existingCode);
    this.updateBlankState();
    this.renderStreaming();
    this.updateSendButton();

    this.streamController = new AbortController();
    let generatedCode = null;
    let proseText = null;

    try {
      const res = await fetch(this.agentUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson',
          'X-CSRF-Token': this.csrfToken()
        },
        credentials: 'same-origin',
        signal: this.streamController.signal,
        body: JSON.stringify({
          prompt: fullPrompt,
          existing_code: existingCode || undefined,
          mode: modeAtSend,
          file_name: this.targetFile,
          other_files: this.otherFiles,
          // Which Workspace apps the prompt actually names (icon clicks and
          // typed words both land here). The server turns these into the Apps
          // Script services the run must use.
          workspace_apps: Array.from(appsPresentInText(promptRaw))
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Generate failed (${res.status})`);
      }

      const codeMode = this.isCodeMode(modeAtSend);
      const hybridMode = this.isHybridMode(modeAtSend);

      await this.consumeNdjson(res, (event) => {
        this.handleStreamEvent(event);
        if (event.type === 'done') {
          if (codeMode) {
            generatedCode = event.code || this.extractCodeFromText(this.streamBuffer);
          }
          if (!codeMode || hybridMode) {
            proseText = (event.text || '').trim() || this.stripCodeFences(this.streamBuffer);
          }
        } else if (event.type === 'error') {
          throw new Error(event.message || 'Agent run failed.');
        }
      });

      let turn;
      if (hybridMode) {
        // No code came back: the cause was a setting, an authorization, a missing
        // trigger. That is a complete answer here, so it renders as prose.
        if (!generatedCode && !proseText) {
          throw new Error('Agent finished but returned nothing. Try again.');
        }
        if (generatedCode) {
          const previousCode = this.workingCode || '';
          turn = this.buildTurn(promptRaw, previousCode, generatedCode, modeAtSend);
          turn.notes = proseText;
          this.workingCode = generatedCode;
        } else {
          turn = this.buildProseTurn(promptRaw, proseText, modeAtSend);
        }
      } else if (codeMode) {
        if (!generatedCode) {
          throw new Error('Agent finished but no Apps Script was found. Try a clearer prompt.');
        }
        const previousCode = this.workingCode || '';
        turn = this.buildTurn(promptRaw, previousCode, generatedCode, modeAtSend);
        // Draft never becomes the working copy — later turns still diff against
        // (and push from) the code that is actually in the sheet.
        if (turn.pushable !== false) this.workingCode = generatedCode;
      } else {
        if (!proseText) {
          throw new Error('Agent finished but returned nothing. Try again.');
        }
        turn = this.buildProseTurn(promptRaw, proseText, modeAtSend);
      }

      this.turns.push(turn);
      if (turn.kind === 'code') {
        this.panelTurnIndex = this.turns.length - 1;
        this.panelTab = 'code';
      }
      if (this.hasPromptInputTarget) this.promptInputTarget.value = '';
      this.renderTurns();
      this.renderStatusLine();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showComposerError(error.message || 'Generate failed.');
      }
    } finally {
      this.phase = 'idle';
      this.streamController = null;
      this.updateBlankState();
      this.renderStreaming();
      this.updateSendButton();
    }
  }

  async consumeNdjson(response, onEvent) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex = buffer.indexOf('\n');
      while (newlineIndex >= 0) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line) onEvent(JSON.parse(line));
        newlineIndex = buffer.indexOf('\n');
      }
    }

    const tail = buffer.trim();
    if (tail) onEvent(JSON.parse(tail));
  }

  extractCodeFromText(text) {
    const fence = new RegExp('```(?:javascript|js|gs)?\\s*\\n([\\s\\S]*?)```', 'i');
    const match = text.match(fence);
    return match ? match[1].trim() : null;
  }

  // Fallback for hybrid modes when the server sent no text: keep the prose the
  // user reads and drop the fenced file, which the card renders separately.
  stripCodeFences(text) {
    return (text || '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  extractFunctionNames(code) {
    if (!code) return [];
    const names = [];
    const re = /function\s+([A-Za-z_$][\w$]*)\s*\(/g;
    let match;
    while ((match = re.exec(code))) {
      if (!names.includes(match[1])) names.push(match[1]);
    }
    return names;
  }

  needsDriveService(code) {
    return /\bDriveApp\.|\bDrive\.Files\b/.test(code || '');
  }

  buildTurn(prompt, previousCode, code, mode = this.mode) {
    const ops = diffLines(previousCode, code);
    const { added, removed } = diffCounts(ops);
    const fnNames = this.extractFunctionNames(code);
    const prevFnNames = this.extractFunctionNames(previousCode);
    const netNew = fnNames.filter((name) => !prevFnNames.includes(name));
    const changedFns = mode === 'comment' ? fnNames : previousCode && netNew.length > 0 ? netNew : fnNames;
    const countLabel = changedFns.length;
    const verb = MODE_META[mode]?.verb || 'Wrote';
    const title =
      mode === 'comment'
        ? `Commented ${fnNames.length} function${fnNames.length === 1 ? '' : 's'}`
        : mode === 'fix'
        ? 'Fixed the script'
        : `${verb} ${countLabel} function${countLabel === 1 ? '' : 's'}`;
    const BLURBS = {
      comment: 'Comments only — no logic changed. Same functions, same behaviour, now documented.',
      fix: `The fix goes into ${this.fileLabel}. Read what changed before you add it.`,
      write: `Goes into ${this.fileLabel}, alongside what is already in it. Every other file in your Apps Script project is left exactly as it is.`
    };
    const blurb = this.standalone
      ? `Not connected to a sheet. Copy the whole file and paste it over ${this.fileLabel} in Extensions → Apps Script.`
      : BLURBS[mode] || BLURBS.write;

    const FN_DISPLAY_LIMIT = 3;
    const fnsFull = changedFns.join(' · ') || '—';
    const fnsDisplay =
      changedFns.length > FN_DISPLAY_LIMIT
        ? `${changedFns.slice(0, FN_DISPLAY_LIMIT).join(' · ')} +${changedFns.length - FN_DISPLAY_LIMIT} more`
        : fnsFull;

    // Write is additive now, but the agent is still a language model: verify
    // rather than trust. Anything that was in the file and is not in this
    // version would be deleted by a push, so the card says so and the push
    // stops to ask.
    const lostFns = prevFnNames.filter((name) => !fnNames.includes(name));

    return {
      prompt,
      title,
      lostFns,
      fns: fnsDisplay,
      fnsFull,
      delta: `+${added} −${removed}`,
      blurb,
      code,
      previousCode,
      mode,
      // Pinned at generation time: a turn always pushes to the file it was
      // written against, even if the user switches target afterwards.
      file: this.targetFile,
      kind: 'code',
      pushable: PUSHABLE_MODES.includes(mode),
      needsDrive: this.needsDriveService(code),
      pushed: false,
      codeOpen: false
    };
  }

  buildProseTurn(prompt, text, mode) {
    return {
      prompt,
      kind: 'prose',
      mode,
      title: MODE_META[mode]?.title || 'Notes',
      text,
      file: this.targetFile,
      // Plan is the one prose mode the user can commit: its notes go into the
      // target file as a comment block, never as code.
      notable: mode === 'plan',
      pushed: false
    };
  }

  // Wraps markdown notes in a single JSDoc-style block. `*/` inside the notes is
  // defanged so it cannot terminate the comment early and expose text as code.
  notesAsComment(text) {
    const stamp = new Date().toISOString().slice(0, 10);
    const body = String(text || '')
      .replace(/\*\//g, '*\u200b/')
      .replace(/\r\n/g, '\n')
      .split('\n')
      .map((line) => ` * ${line}`.replace(/\s+$/, ''))
      .join('\n');
    return [
      PLAN_NOTES_BEGIN,
      `/**`,
      ` * Better Sheets Automate — Plan (${stamp})`,
      ` * Notes only. No executable code below this block came from Plan mode.`,
      ` *`,
      body,
      ` */`,
      PLAN_NOTES_END
    ].join('\n');
  }

  // Removes a previously pushed Plan block, so notes stay a single current
  // snapshot rather than accumulating one block per push.
  stripPlanNotes(code) {
    const re = new RegExp(`\\n*${PLAN_NOTES_BEGIN}[\\s\\S]*?${PLAN_NOTES_END}\\n*`, 'g');
    return String(code || '').replace(re, '\n');
  }

  // ---------- turns ----------

  renderTurns() {
    this.renderSplit();
    if (!this.hasTurnsListTarget) return;
    this.turnsListTarget.innerHTML = this.turns
      .map((turn, index) => (turn.kind === 'prose' ? this.proseCardHtml(turn, index) : this.turnCardHtml(turn, index)))
      .join('');
    this.updateBlankState();
  }

  turnCardHtml(turn, index) {
    if (this.standalone) return this.standaloneTurnHtml(turn, index);

    let pushedBlock;
    if (turn.pushable === false) {
      pushedBlock = `<span class="inline-flex items-center gap-2 text-[15px] text-[#64748b]"><span aria-hidden="true">🔒</span>Not applied — your script is untouched</span>`;
    } else if (turn.pushed) {
      pushedBlock = `<span class="inline-flex items-center gap-2 text-[15.5px] font-semibold text-[#6ee7b7]"><span class="w-[22px] h-[22px] rounded-full bg-[#10b981] inline-flex items-center justify-center text-[#022c22] text-[12px] font-bold">✓</span>In your sheet — reload it and open the Automate menu</span>`;
    } else {
      pushedBlock = `<button type="button" data-action="click->automate#pushTurnCode" data-turn-index="${index}" class="h-[40px] px-4 rounded-lg border-0 bg-[#10b981] text-white text-[15.5px] font-semibold cursor-pointer hover:bg-[#059669]">Add to ${this.escapeHtml(turn.file || this.targetFile)}.gs</button>`;
    }

    const driveButton = turn.needsDrive
      ? `<button type="button" data-action="click->automate#openLayer" data-layer="setup" class="ml-auto border-0 bg-transparent p-0 text-[15px] text-[#fcd34d] cursor-pointer">Needs Drive service</button>`
      : '';

    const codeBlock = turn.codeOpen
      ? `<div class="mt-[13px] border border-[#1f2937] rounded-[10px] bg-[rgba(2,6,23,0.8)] overflow-hidden animate-[om-fade_0.25s_ease-out]">
          <div class="flex items-center gap-2.5 px-3 py-[9px] border-b border-[#111827]">
            <span class="font-mono text-[14px] text-[#e2e8f0]">${this.escapeHtml(turn.file || this.targetFile)}.gs</span>
            <span class="text-[14px] text-[#64748b]">${turn.code.split('\n').length} lines after this change</span>
            <button type="button" data-action="click->automate#toggleTurnCode" data-turn-index="${index}" class="ml-auto border-0 bg-transparent p-0 text-[14px] text-[#94a3b8] cursor-pointer">Hide</button>
          </div>
          <pre class="m-0 px-3.5 py-3 max-h-[300px] overflow-auto font-mono text-[14.5px] leading-[1.7] text-[#cbd5e1]">${this.escapeHtml(turn.code)}</pre>
        </div>`
      : '';

    const lostBlock = turn.lostFns?.length
      ? `<div class="mt-[11px] rounded-lg border border-[rgba(248,113,113,0.5)] bg-[rgba(127,29,29,0.25)] px-3.5 py-2.5 text-[15px] leading-[1.55] text-[#fca5a5]">
          <span class="font-semibold">${turn.lostFns.length} function${
            turn.lostFns.length === 1 ? '' : 's'
          } would be deleted:</span>
          <span class="font-mono">${this.escapeHtml(turn.lostFns.map((name) => `${name}()`).join(' · '))}</span>
          — ${this.escapeHtml(turn.file || this.targetFile)}.gs has ${
            turn.lostFns.length === 1 ? 'it' : 'them'
          } now, this version does not. Ask again and say to keep everything already in the file.
        </div>`
      : '';

    // Fix explains itself to the user here, above the card — deliberately not as
    // comments inside their script.
    const notesBlock = turn.notes
      ? `<div class="automate-prose min-w-0 mb-3 pb-3 border-b border-[#1f2937]">${renderMarkdown(turn.notes)}</div>`
      : '';

    return `
      <div class="w-full flex flex-col gap-3 animate-[om-rise_0.35s_ease-out]" data-turn-index="${index}">
        <div class="self-start max-w-[80%] border border-[#1f2937] rounded-[10px] bg-[rgba(30,41,59,0.5)] px-3.5 py-[11px] text-[17px] leading-[1.55] text-[#f1f5f9]">${this.escapeHtml(turn.prompt)}</div>
        <div class="border border-[#1f2937] rounded-xl bg-[rgba(11,18,32,0.75)] px-4 py-3.5">
          ${notesBlock}
          <div class="flex items-center gap-2.5">
            <span class="text-[17px] font-semibold text-[#f1f5f9]">${this.escapeHtml(turn.title)}</span>
            <span class="font-mono text-[14px] text-[#64748b]">${this.escapeHtml(turn.fns)}</span>
            <span class="ml-auto font-mono text-[14px] text-[#6ee7b7]">${this.escapeHtml(turn.delta)}</span>
          </div>
          <div class="mt-[5px] text-[15.5px] leading-[1.55] text-[#94a3b8]">${this.escapeHtml(turn.blurb)}</div>
          ${lostBlock}
          <div class="flex items-center gap-2.5 mt-[13px]">
            ${pushedBlock}
            <button type="button" data-action="click->automate#toggleTurnCode" data-turn-index="${index}" class="border-0 bg-transparent p-0 text-[15px] text-[#34d399] cursor-pointer">${turn.codeOpen ? 'Hide code' : 'Code'}</button>
            <button type="button" data-action="click->automate#openTurnDiff" data-turn-index="${index}" class="border-0 bg-transparent p-0 text-[15px] text-[#34d399] cursor-pointer">What changed</button>
            <button type="button" data-action="click->automate#copyTurnCode" data-turn-index="${index}" class="border-0 bg-transparent p-0 text-[15px] text-[#34d399] cursor-pointer">Copy code</button>
            ${driveButton}
          </div>
          ${codeBlock}
        </div>
      </div>`;
  }

  proseCardHtml(turn, index) {
    const BADGE = {
      plan: 'text-[#6ee7b7] border-[rgba(52,211,153,0.35)] bg-[rgba(16,185,129,0.08)]',
      map: 'text-[#93c5fd] border-[rgba(147,197,253,0.3)] bg-[rgba(59,130,246,0.08)]',
      ask: 'text-[#fcd34d] border-[rgba(252,211,77,0.3)] bg-[rgba(252,211,77,0.08)]',
      fix: 'text-[#fca5a5] border-[rgba(252,165,165,0.3)] bg-[rgba(239,68,68,0.08)]'
    };

    return `
      <div class="w-full flex flex-col gap-3 animate-[om-rise_0.35s_ease-out]">
        <div class="self-start max-w-[80%] border border-[#1f2937] rounded-[10px] bg-[rgba(30,41,59,0.5)] px-3.5 py-[11px] text-[17px] leading-[1.55] text-[#f1f5f9]">${this.escapeHtml(turn.prompt)}</div>
        <div class="border border-[#1f2937] rounded-xl bg-[rgba(11,18,32,0.75)] px-4 py-3.5">
          <div class="flex items-center gap-2.5 mb-1">
            <span class="inline-flex items-center h-[24px] px-2 rounded-full border text-[13px] font-semibold ${BADGE[turn.mode] || BADGE.plan}">${this.escapeHtml(turn.title)}</span>
          </div>
          <div class="automate-prose min-w-0">${renderMarkdown(turn.text)}</div>
          ${
            turn.notable && !this.standalone
              ? turn.pushed
                ? `<div class="flex items-center gap-2 mt-3 text-[15px] font-semibold text-[#fde68a]"><span aria-hidden="true">&#128221;</span>Added to ${this.escapeHtml(turn.file || this.targetFile)}.gs as a comment block</div>`
                : `<div class="flex items-center gap-3 mt-3 flex-wrap">
                     <button type="button" data-action="click->automate#pushNotes" data-turn-index="${index}" class="h-[40px] px-4 rounded-lg border-0 bg-[#f59e0b] text-[#451a03] text-[15.5px] font-semibold cursor-pointer hover:bg-[#d97706]">Add notes to ${this.escapeHtml(turn.file || this.targetFile)}.gs</button>
                     <span class="text-[15px] text-[#64748b]">Written as comments, replacing any earlier Plan notes. Your code is not touched.</span>
                   </div>`
              : ''
          }
        </div>
      </div>`;
  }

  // Takes the file without writing it anywhere — paste it into the Apps Script
  // editor yourself, or keep it. Nothing is pushed.
  async copyTurnCode(event) {
    const button = event.currentTarget;
    const turn = this.turns[Number(button.dataset.turnIndex)];
    if (!turn?.code) return;

    const done = (label) => {
      button.textContent = label;
      setTimeout(() => {
        button.textContent = 'Copy code';
      }, 1600);
    };

    try {
      await navigator.clipboard.writeText(turn.code);
      done('Copied');
    } catch {
      // Clipboard blocked (insecure context, denied permission): open the code
      // so it can be selected by hand rather than failing silently.
      turn.codeOpen = true;
      this.renderTurns();
      this.showComposerError('Could not reach the clipboard. The code is open below — select and copy it.');
    }
  }

  toggleTurnCode(event) {
    const turn = this.turns[Number(event.currentTarget.dataset.turnIndex)];
    if (!turn) return;
    turn.codeOpen = !turn.codeOpen;
    this.renderTurns();
  }

  openTurnDiff(event) {
    const turn = this.turns[Number(event.currentTarget.dataset.turnIndex)];
    if (!turn) return;
    const ops = diffLines(turn.previousCode, turn.code);
    this.currentDiffHunks = diffHunks(ops, 1);
    this.currentDiffIndex = 0;
    if (this.hasDiffFileNameTarget) {
      this.diffFileNameTarget.textContent = `${turn.file || this.targetFile}.gs`;
    }
    this.renderDiffModal();
    this.setLayer('diff');
  }

  diffPrev() {
    if (this.currentDiffIndex > 0) {
      this.currentDiffIndex -= 1;
      this.renderDiffModal();
    }
  }

  diffNext() {
    if (this.currentDiffIndex < this.currentDiffHunks.length - 1) {
      this.currentDiffIndex += 1;
      this.renderDiffModal();
    }
  }

  renderDiffModal() {
    if (!this.hasDiffBodyTarget) return;
    const total = this.currentDiffHunks.length;

    if (!total) {
      this.diffBodyTarget.innerHTML = `<div class="px-4 py-6 text-[15px] text-[#64748b]">No changes to show.</div>`;
      if (this.hasDiffPositionTarget) this.diffPositionTarget.textContent = '';
      return;
    }

    const hunk = this.currentDiffHunks[this.currentDiffIndex];
    this.diffBodyTarget.innerHTML = hunk
      .map((op) => {
        const text = this.escapeHtml(op.line || ' ');
        if (op.type === 'add') {
          return `<div class="px-4 bg-[rgba(16,185,129,0.1)] text-[#6ee7b7]">+ ${text}</div>`;
        }
        if (op.type === 'remove') {
          return `<div class="px-4 bg-[rgba(248,113,113,0.1)] text-[#fca5a5]">− ${text}</div>`;
        }
        return `<div class="px-4 text-[#475569]">  ${text}</div>`;
      })
      .join('');

    if (this.hasDiffPositionTarget) {
      this.diffPositionTarget.textContent = `${this.currentDiffIndex + 1} of ${total}`;
    }
  }

  // ---------- writing outside Better.gs ----------

  // Better.gs is Automate's own file: writing it can only clobber Automate's
  // own earlier work, so it goes through without a question. Any other file is
  // the user's, and a push replaces it, so ask first — once per sheet.
  needsCodeWriteConfirm(file) {
    if (this.codeWriteConfirmed) return false;
    return normalizeTargetFile(file) !== DEFAULT_TARGET_FILE;
  }

  // Names the handlers already in the file, so the question is about their own
  // code rather than about a file name.
  existingHandlerNames() {
    const code = this.workingCode || this.scriptCode || '';
    const names = [];
    for (const match of code.matchAll(/function\s+(on[A-Z][A-Za-z0-9_]*)\s*\(/g)) {
      if (!names.includes(match[1])) names.push(match[1]);
    }
    return names;
  }

  // One dialog, two questions. onConfirm decides what "yes" means, so a push
  // that trips both checks asks both, in turn.
  askBeforeWrite({ title, text, note = '', confirmLabel = 'Yes, write it', onConfirm }) {
    this.pendingCodeWrite = onConfirm;

    if (this.hasCodeWriteTitleTarget) this.codeWriteTitleTarget.textContent = title;
    if (this.hasCodeWriteTextTarget) this.codeWriteTextTarget.textContent = text;
    if (this.hasCodeWriteNoteTarget) {
      this.codeWriteNoteTarget.textContent = note;
      this.codeWriteNoteTarget.classList.toggle('hidden', !note);
    }
    if (this.hasCodeWriteConfirmButtonTarget) this.codeWriteConfirmButtonTarget.textContent = confirmLabel;

    if (this.hasCodeWriteModalTarget && typeof this.codeWriteModalTarget.showModal === 'function') {
      this.codeWriteModalTarget.showModal();
    } else {
      // No dialog support: better to write than to strand the push.
      onConfirm();
    }
  }

  askCodeWrite(file, resume) {
    const label = `${normalizeTargetFile(file)}.gs`;
    const handlers = this.existingHandlerNames();
    const has = handlers.length
      ? `It already has ${this.listPhrase(handlers.map((name) => `${name}()`))}.`
      : 'That is your own file, not one Automate created.';

    this.askBeforeWrite({
      title: `Write into ${label}?`,
      text: `${label} is where your own code lives. ${has} Pushing replaces the file with the version you just reviewed.`,
      note: 'Better.gs is Automate\u2019s own file and never touches code you wrote.',
      onConfirm: () => {
        this.codeWriteConfirmed = true;
        resume();
      }
    });
  }

  // The last gate before a function disappears from the user's project.
  askFunctionLoss(turn, resume) {
    const names = turn.lostFns.map((name) => `${name}()`);
    const label = `${normalizeTargetFile(turn.file || this.targetFile)}.gs`;

    this.askBeforeWrite({
      title: `Delete ${names.length} function${names.length === 1 ? '' : 's'}?`,
      text: `${this.listPhrase(names)} ${names.length === 1 ? 'is' : 'are'} in ${label} now and ${
        names.length === 1 ? 'is' : 'are'
      } not in this version. A push replaces the whole file, so ${
        names.length === 1 ? 'it' : 'they'
      } would be gone.`,
      note: 'Cancel, then ask again saying to keep everything already in the file.',
      confirmLabel: `Delete ${names.length === 1 ? 'it' : 'them'} and write`,
      onConfirm: resume
    });
  }

  listPhrase(items) {
    if (items.length <= 1) return items.join('');
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  confirmCodeWrite() {
    this.closeCodeWriteModal();
    const onConfirm = this.pendingCodeWrite;
    this.pendingCodeWrite = null;
    if (onConfirm) onConfirm();
  }

  cancelCodeWrite() {
    this.pendingCodeWrite = null;
    this.closeCodeWriteModal();
  }

  closeCodeWriteModal() {
    if (this.hasCodeWriteModalTarget && typeof this.codeWriteModalTarget.close === 'function') {
      this.codeWriteModalTarget.close();
    }
  }

  // What is actually in the file right now, straight from Google. Returns null
  // when it cannot be read — which is itself worth stopping for.
  async fetchLiveFileCode(file) {
    if (!this.scriptId) return null;
    try {
      const url = new URL(this.scriptPreviewUrlValue, window.location.origin);
      url.searchParams.set('script_id', this.scriptId);
      url.searchParams.set('file_name', normalizeTargetFile(file));

      const res = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      return typeof data?.code === 'string' ? data.code : null;
    } catch {
      return null;
    }
  }

  // Runs once per turn, before the first push: compares the file as it stands
  // against what is about to replace it.
  async verifyPush(turn, file, button) {
    if (turn.pushVerified) return;

    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Checking…';

    const live = await this.fetchLiveFileCode(file);
    turn.pushVerified = true;
    turn.pushUnreadable = live === null;
    turn.liveLostFns = live
      ? this.extractFunctionNames(live).filter((name) => !this.extractFunctionNames(turn.code).includes(name))
      : [];

    button.disabled = false;
    button.textContent = label;
  }

  async pushTurnCode(event) {
    const index = Number(event.currentTarget.dataset.turnIndex);
    const turn = this.turns[index];
    if (!turn || turn.pushable === false || !this.spreadsheetId) return;

    const button = event.currentTarget;
    const file = turn.file || this.targetFile;
    const resume = () => this.pushTurnCode({ currentTarget: button });

    await this.verifyPush(turn, file, button);

    if (turn.pushUnreadable && !turn.lossConfirmed) {
      return this.askBeforeWrite({
        title: `Could not read ${normalizeTargetFile(file)}.gs`,
        text: `Automate cannot see what is in ${normalizeTargetFile(
          file
        )}.gs right now, and a push replaces the whole file. Anything in there that is not in this version would be gone.`,
        note: 'Cancel, press Refresh, and try again once it reads.',
        confirmLabel: 'Replace it anyway',
        onConfirm: () => {
          turn.lossConfirmed = true;
          resume();
        }
      });
    }

    const lost = turn.liveLostFns?.length ? turn.liveLostFns : turn.lostFns || [];
    if (lost.length && !turn.lossConfirmed) {
      return this.askFunctionLoss({ ...turn, lostFns: lost }, () => {
        turn.lossConfirmed = true;
        resume();
      });
    }

    if (this.needsCodeWriteConfirm(file)) {
      return this.askCodeWrite(file, resume);
    }

    button.disabled = true;
    button.textContent = 'Pushing…';

    try {
      const res = await fetch(this.pushUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          spreadsheet_id: this.spreadsheetId,
          script_id: this.scriptId || undefined,
          file_name: file,
          code: turn.code
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.error || `Push failed (${res.status})`);
        err.helpUrl = data.help_url;
        err.helpLabel = data.help_label;
        throw err;
      }

      if (data.script_id) {
        this.scriptId = data.script_id;
        this.rememberSheetLink(this.spreadsheetId, data.script_id);
      }
      turn.pushed = true;
      this.renderTurns();
    } catch (error) {
      this.showComposerError(error.message || 'Push failed.', error.helpUrl, error.helpLabel);
      button.disabled = false;
      button.textContent = `Add to ${turn.file || this.targetFile}.gs`;
    }
  }

  // Pushes Plan notes as a comment block. Critically, this sends the current
  // code WITH the notes appended: the push endpoint replaces the Automate block
  // wholesale, so sending notes alone would wipe the code inside it.
  async pushNotes(event) {
    const index = Number(event.currentTarget.dataset.turnIndex);
    const turn = this.turns[index];
    if (!turn || !turn.notable || turn.pushed || !this.spreadsheetId) return;

    // Notes are appended to the working copy of the file they were written for.
    // After a target switch that copy is a different file, so refuse rather than
    // graft the notes onto the wrong one.
    const file = turn.file || this.targetFile;
    if (file !== this.targetFile) {
      this.showComposerError(
        `These notes were written for ${file}.gs. Switch back to ${file}.gs to add them.`
      );
      return;
    }

    const button = event.currentTarget;
    if (this.needsCodeWriteConfirm(file)) {
      return this.askCodeWrite(file, () => this.pushNotes({ currentTarget: button }));
    }

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const existing = this.stripPlanNotes(this.workingCode || '').replace(/\s+$/, '');
      const combined = existing
        ? `${existing}\n\n${this.notesAsComment(turn.text)}\n`
        : `${this.notesAsComment(turn.text)}\n`;

      const res = await fetch(this.pushUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          spreadsheet_id: this.spreadsheetId,
          script_id: this.scriptId || undefined,
          file_name: file,
          code: combined
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.error || `Could not add notes (${res.status})`);
        err.helpUrl = data.help_url;
        err.helpLabel = data.help_label;
        throw err;
      }

      if (data.script_id) {
        this.scriptId = data.script_id;
        this.rememberSheetLink(this.spreadsheetId, data.script_id);
      }
      this.workingCode = combined;
      turn.pushed = true;
      this.renderTurns();
    } catch (error) {
      this.showComposerError(error.message || 'Could not add notes.', error.helpUrl, error.helpLabel);
      button.disabled = false;
      button.textContent = `Add notes to ${file}.gs`;
    }
  }

  // ---------- overlays ----------

  openLayer(event) {
    const layer = event.currentTarget.dataset.layer;
    if (!layer) return;
    this.setLayer(layer);
  }

  closeLayer() {
    this.setLayer(null);
  }

  setLayer(layer) {
    this.layer = layer;
    const map = {
      diff: this.hasDiffModalTarget ? this.diffModalTarget : null,
      help: this.hasHelpModalTarget ? this.helpModalTarget : null,
      context: this.hasContextPanelTarget ? this.contextPanelTarget : null,
      setup: this.hasSetupPanelTarget ? this.setupPanelTarget : null,
      instructions: this.hasInstructionsPanelTarget ? this.instructionsPanelTarget : null
    };

    Object.entries(map).forEach(([key, el]) => {
      if (!el) return;
      el.classList.toggle('hidden', key !== layer);
    });
    if (this.hasScrimTarget) this.scrimTarget.classList.toggle('hidden', !layer);

    if (layer === 'context') this.renderContextPanel();
    if (layer === 'instructions') this.loadInstructions();
  }

  renderContextPanel() {
    if (this.hasContextSheetNameTarget) this.contextSheetNameTarget.textContent = this.spreadsheetName || 'Your spreadsheet';
    if (this.hasStatusSheetLinkTarget) this.statusSheetLinkTarget.href = this.spreadsheetUrl || '#';
    if (this.hasContextSheetIdTarget) {
      const id = this.spreadsheetId || '';
      this.contextSheetIdTarget.textContent = id.length > 6 ? `${id.slice(0, 4)}…${id.slice(-2)}` : id;
    }
    if (this.hasContextTabsListTarget) {
      const sheets = [
        { id: this.spreadsheetId, name: this.spreadsheetName, tabs: this.sheetTabs, primary: true },
        ...this.extraSpreadsheets
      ];
      this.contextTabsListTarget.innerHTML = sheets.map((sheet) => this.contextSheetBlockHtml(sheet)).join('');
    }
  }

  loadInstructions() {
    if (!this.hasInstructionsTextTarget) return;
    fetch(this.instructionsUrlValue, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        if (this.hasInstructionsTextTarget) this.instructionsTextTarget.value = data.instructions || '';
      })
      .catch(() => {});
  }

  saveInstructions() {
    if (!this.hasInstructionsTextTarget) return;
    const text = this.instructionsTextTarget.value;
    fetch(this.instructionsUrlValue, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken(), Accept: 'application/json' },
      body: JSON.stringify({ instructions: text })
    })
      .then((r) => r.json())
      .then((data) => {
        if (!this.hasInstructionsSavedLabelTarget) return;
        if (data.ok) {
          this.instructionsSavedLabelTarget.textContent = 'Saved';
          setTimeout(() => { if (this.hasInstructionsSavedLabelTarget) this.instructionsSavedLabelTarget.textContent = ''; }, 2000);
        } else {
          this.instructionsSavedLabelTarget.textContent = data.error || 'Error saving';
        }
      })
      .catch(() => {
        if (this.hasInstructionsSavedLabelTarget) this.instructionsSavedLabelTarget.textContent = 'Error saving';
      });
  }

  contextSheetBlockHtml(sheet) {
    // Tokens come from the shared mention list so a name that collides across
    // spreadsheets inserts qualified here too.
    const tokens = new Map(
      this.mentionables.map((mentionable) => [
        tabKey(mentionable.sheetId, mentionable.sheetName, mentionable.tabName),
        mentionable.token
      ])
    );

    const tabsHtml = (sheet.tabs || [])
      .map((tab) => {
        const headerText =
          tab.headers && tab.headers.length
            ? this.escapeHtml(tab.headers.join(' · '))
            : `<span class="text-[#475569]">row 1 is empty — no headers</span>`;
        const token = tokens.get(tabKey(sheet.id, sheet.name, tab.name)) || tab.name;
        return `
          <button type="button"
                  data-action="click->automate#insertTabMention"
                  data-mention-token="${this.escapeHtml(token)}"
                  title="Reference ${this.escapeHtml(tab.name)} in your prompt"
                  class="group w-full text-left border border-[#1f2937] rounded-lg bg-[rgba(11,18,32,0.7)] px-3 py-2.5 cursor-pointer hover:border-[rgba(52,211,153,0.45)] hover:bg-[rgba(16,185,129,0.07)]">
            <span class="flex items-center gap-2">
              <span class="text-[15px] font-semibold text-[#e2e8f0]">${this.escapeHtml(tab.name)}</span>
              <span class="ml-auto font-mono text-[13px] text-[#475569] group-hover:text-[#6ee7b7]">@${this.escapeHtml(
                token
              )}</span>
            </span>
            <span class="block mt-1 font-mono text-[14px] leading-[1.6] text-[#94a3b8]">${headerText}</span>
          </button>`;
      })
      .join('');

    const linkBtn =
      'border-0 bg-transparent p-0 text-[14.5px] cursor-pointer';

    const header = sheet.primary
      ? `<div class="flex items-center gap-3 flex-wrap">
           <span class="inline-flex items-center gap-1.5 text-[14.5px] text-[#6ee7b7]"><span class="w-2 h-2 rounded-full bg-[#10b981]"></span>Writing to this sheet</span>
           <button type="button" data-action="click->automate#clearSheet" class="${linkBtn} text-[#fca5a5] ml-auto">Clear &amp; pick another</button>
         </div>`
      : `<div class="flex items-center gap-3 flex-wrap">
           <div class="text-[15.5px] font-semibold text-[#f1f5f9]">${this.escapeHtml(sheet.name || 'Spreadsheet')}</div>
           <div class="ml-auto flex items-center gap-3">
             <button type="button" data-action="click->automate#useSpreadsheet" data-spreadsheet-id="${this.escapeHtml(sheet.id || '')}" class="${linkBtn} text-[#34d399]">Write to this</button>
             <button type="button" data-action="click->automate#removeSpreadsheet" data-spreadsheet-id="${this.escapeHtml(sheet.id || '')}" class="${linkBtn} text-[#64748b]">Remove</button>
           </div>
         </div>`;

    return `
      <div class="flex flex-col gap-2 pb-3 mb-1 border-b border-[#111827] last:border-b-0">
        ${header}
        ${tabsHtml}
      </div>`;
  }

  async addSpreadsheet(event) {
    event?.preventDefault();
    this.clearAddSpreadsheetError();

    await this.pickSpreadsheet({
      onPick: (doc) => this.loadExtraSpreadsheet(doc.id, doc.name),
      onError: (message) => this.showAddSpreadsheetError(message)
    });
  }

  async loadExtraSpreadsheet(id, name) {
    if (!id) return;
    if (this.extraSpreadsheets.some((sheet) => sheet.id === id)) return;

    try {
      const url = new URL(this.spreadsheetPreviewUrlValue, window.location.origin);
      url.searchParams.set('spreadsheet_id', id);
      const res = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not read spreadsheet.');

      this.extraSpreadsheets.push({
        id,
        name: data.title || name || 'Spreadsheet',
        tabs: (data.sheets || []).map((sheet) => ({ name: sheet.name, headers: sheet.headers || [] }))
      });
      this.renderContextPanel();
    } catch (error) {
      this.showAddSpreadsheetError(error.message || 'Could not read that spreadsheet.');
    }
  }

  showAddSpreadsheetError(message) {
    if (!this.hasAddSpreadsheetErrorTarget) return;
    this.addSpreadsheetErrorTarget.textContent = message;
    this.addSpreadsheetErrorTarget.classList.remove('hidden');
  }

  clearAddSpreadsheetError() {
    if (!this.hasAddSpreadsheetErrorTarget) return;
    this.addSpreadsheetErrorTarget.textContent = '';
    this.addSpreadsheetErrorTarget.classList.add('hidden');
  }

  // ---------- errors ----------

  showSheetError(message, helpUrl = null, helpLabel = null) {
    if (!this.hasSheetErrorTarget) return;
    this.renderMessage(this.sheetErrorTextTarget, message, helpUrl, helpLabel);
    this.sheetErrorTarget.classList.remove('hidden');
  }

  clearSheetError() {
    if (!this.hasSheetErrorTarget) return;
    this.sheetErrorTarget.classList.add('hidden');
  }

  showScriptError(message, helpUrl = null, helpLabel = null) {
    if (!this.hasScriptErrorTarget) return;
    this.renderMessage(this.scriptErrorTextTarget, message, helpUrl, helpLabel);
    this.scriptErrorTarget.classList.remove('hidden');
  }

  clearScriptError() {
    if (!this.hasScriptErrorTarget) return;
    this.scriptErrorTarget.classList.add('hidden');
  }

  showComposerError(message, helpUrl = null, helpLabel = null) {
    if (!this.hasComposerErrorTarget) return;
    this.renderMessage(this.composerErrorTextTarget, message, helpUrl, helpLabel);
    this.composerErrorTarget.classList.remove('hidden');
  }

  clearComposerError() {
    if (!this.hasComposerErrorTarget) return;
    this.composerErrorTarget.classList.add('hidden');
  }

  renderMessage(element, message, helpUrl = null, helpLabel = null) {
    if (!element) return;
    const text = this.escapeHtml(message || '');
    if (helpUrl && helpLabel) {
      const url = this.escapeHtml(helpUrl);
      const label = this.escapeHtml(helpLabel);
      element.innerHTML = `${text} <a href="${url}" target="_blank" rel="noopener" class="text-[#34d399] underline">${label}</a>`;
      return;
    }
    element.textContent = message;
  }

  // ---------- utils ----------

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
